import { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Stack, Typography, Avatar, Chip,
  TextField, MenuItem, Button, IconButton, Tooltip, CircularProgress, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  TablePagination, TableFooter,
  Dialog, DialogContent, DialogActions, DialogTitle,
  InputAdornment, Divider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  MenuBook as CashbookIcon, AccountBalance as BankIcon,
  Payments as PaymentIcon, AddCard as DepositIcon,
  TrendingUp as InflowIcon, TrendingDown as OutflowIcon,
  PriceCheck as CashIcon, Refresh as RefreshIcon, Edit as EditIcon,
  Delete as DeleteIcon, North as InIcon, South as OutIcon,
  CalendarMonth as MonthIcon, Add as AddIcon, Close as CloseIcon,
  Print as PrintIcon, Download as DownloadIcon,
  EventBusy as LockedIcon, ArrowForward as BFIcon, Search as SearchIcon,
  Description as VoucherIcon, Save as SaveIcon, Cancel as CancelIcon,
  Receipt as DescriptionIcon,
  AccountTree as DoubleEntryIcon, Visibility as ViewIcon,
  SwapHoriz as TransferIcon, ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import ModuleHeader from '../../components/ModuleHeader';
import { useIsMobile, MobileDataCard } from '../../components/ResponsiveTable';
import API from '../../BaseURL';
import swal from '../../utils/swal';
// Ask before you spend. When expenditure approval is on, nothing reaches
// the journal on this page until a request here has been signed off.
import { ExpenditureRequestsPanel } from './ExpenditureRequests';

const FONT_ACCT = `'IBM Plex Sans', 'Inter', sans-serif`;
const FONT_MONO = `'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace`;
const TNUM = { fontVariantNumeric: 'tabular-nums' };
const fmtMoney = (n) =>
  Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? String(d) : dt.toLocaleDateString();
};

const MON_NAMES = ['January','February','March','April','May','June',
                   'July','August','September','October','November','December'];

function monthYerOf(date) {
  const d = date ? new Date(date) : new Date();
  return `${MON_NAMES[d.getMonth()]}/${d.getFullYear()}`;
}

const CASH = 'Cash-In-Hand';

// ── Source label & badge for an opening balance row ─────────────
// Translates the backend's `source` enum into UX copy.
function sourceMeta(source) {
  switch (source) {
    case 'confirmed':
      return { label: 'Confirmed', tip: 'A B/F row exists in cash_book for this month.', color: 'success' };
    case 'computed':
      return { label: 'Computed', tip: 'Derived from the previous month\'s closing balance. Click "Confirm carry" to materialise it as a B/F row.', color: 'primary' };
    case 'legacy':
      return { label: 'Legacy', tip: 'Read from the old cash_bf / bank_b_f seed table. Confirm to bind it to this month.', color: 'warning' };
    case 'opening_balance':
      return { label: 'Account opening', tip: 'Read from the account\'s registered opening balance.', color: 'secondary' };
    default:
      return { label: 'No carry', tip: 'No prior balance found for this account.', color: 'default' };
  }
}

function SourceRow({ title, avatar, amount, source, bf_from, tone = 'primary', dense = false, onConfirm }) {
  const meta = sourceMeta(source);
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{
      p: dense ? 1.1 : 1.5, borderRadius: 1.5, mb: 0.7,
      bgcolor: source === 'confirmed'
        ? (t) => alpha(t.palette[tone].main, 0.06)
        : 'transparent',
      border: '1px solid',
      borderColor: source === 'confirmed'
        ? (t) => alpha(t.palette[tone].main, 0.25)
        : 'divider',
    }}>
      <Avatar sx={{ width: dense ? 30 : 36, height: dense ? 30 : 36,
        bgcolor: `${tone}.main`, fontSize: dense ? 14 : 16 }}>
        {avatar}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.7}>
          <Typography fontWeight={800} fontSize={dense ? 13 : 14} noWrap>{title}</Typography>
          <Tooltip title={meta.tip}>
            <Chip size="small" color={meta.color} variant={source === 'confirmed' ? 'filled' : 'outlined'}
              label={meta.label}
              sx={{ height: 18, fontSize: 9.5, fontWeight: 800, '& .MuiChip-label': { px: 0.7 } }} />
          </Tooltip>
        </Stack>
        <Typography variant="caption" color="text.secondary" noWrap>
          {bf_from
            ? `Carried from ${bf_from}`
            : (source === 'opening_balance' ? 'Initial account opening balance' : '—')}
        </Typography>
      </Box>
      <Typography fontWeight={800} sx={{ ...TNUM, fontSize: dense ? 14 : 16,
        color: amount > 0 ? `${tone}.dark` : 'text.disabled' }}>
        GHS {fmtMoney(amount)}
      </Typography>
      {onConfirm && (
        <Button size="small" variant="outlined" color={tone}
          onClick={onConfirm}
          sx={{ fontWeight: 800, textTransform: 'none' }}>
          Confirm carry
        </Button>
      )}
    </Stack>
  );
}

// ── KPI tile ───────────────────────────────────────────────────
function KpiTile({ icon, label, value, sub, tone = 'primary' }) {
  return (
    <Card sx={{
      flex: 1, minWidth: 200, borderRadius: 2,
      borderLeft: (t) => `4px solid ${t.palette[tone].main}`,
      transition: 'transform .15s, box-shadow .15s',
      '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
    }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.6, py: 1.6 }}>
        <Box sx={{
          width: 46, height: 46, borderRadius: '50%',
          bgcolor: (t) => alpha(t.palette[tone].main, 0.14),
          color: `${tone}.main`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>{icon}</Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary"
            sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 10, display: 'block' }}>
            {label}
          </Typography>
          <Typography sx={{ fontWeight: 800, ...TNUM, fontSize: 20, lineHeight: 1.15 }}>
            {value}
          </Typography>
          {sub && <Typography variant="caption" color="text.secondary" noWrap>{sub}</Typography>}
        </Box>
      </CardContent>
    </Card>
  );
}

// ── Live double-entry preview used by Deposit & Payment dialogs ──
// `dr` and `cr` accept either ONE { account, kind, amount? } or an
// ARRAY of those (split-cheque case). When `amount` is provided as a
// top-level prop, single-row sides use it directly; for multi-row
// sides each entry must carry its own `amount`.
function JournalPreview({ kind, dr, cr, amount, hint }) {
  const headTotal = Number(amount) || 0;
  const drArr = Array.isArray(dr) ? dr.filter(Boolean) : (dr ? [dr] : []);
  const crArr = Array.isArray(cr) ? cr.filter(Boolean) : (cr ? [cr] : []);

  // Normalise lines: if an entry has its own .amount use it, else use head.
  const normalise = (arr) => arr.map(l => ({
    account: l?.account || '',
    kind:    l?.kind || '',
    amount:  Number(l?.amount ?? headTotal) || 0,
  }));
  const drLines = normalise(drArr);
  const crLines = normalise(crArr);

  // Total of each side: if multi-line, sum; if single, use head amount.
  const drTotal = drArr.length > 1
    ? drLines.reduce((s, l) => s + l.amount, 0)
    : (drLines[0]?.account ? headTotal : 0);
  const crTotal = crArr.length > 1
    ? crLines.reduce((s, l) => s + l.amount, 0)
    : (crLines[0]?.account ? headTotal : 0);

  const ready    = drLines.some(l => l.account) && crLines.some(l => l.account) && headTotal > 0;
  const balanced = ready && Math.abs(drTotal - crTotal) < 0.01 && Math.abs(drTotal - headTotal) < 0.01;

  // Renders one side. `lines` already normalised; `tone` is 'primary' (DR) or 'success' (CR).
  const renderSide = (lines, tone, label) => (
    <Box sx={{
      ...(tone === 'primary' ? { borderRight: '1px solid', borderColor: 'divider' } : {}),
      bgcolor: (t) => alpha(t.palette[tone].main, 0.04),
    }}>
      <Box sx={{
        px: 1.5, py: 0.6,
        bgcolor: (t) => alpha(t.palette[tone].main, 0.08),
        borderBottom: '1px solid', borderColor: 'divider',
      }}>
        <Typography variant="caption" fontWeight={800}
          sx={{ letterSpacing: 1, fontSize: 10, color: `${tone}.main` }}>
          {label}
        </Typography>
      </Box>
      <Box sx={{ px: 0, py: 0, minHeight: 56 }}>
        {lines.length === 0 ? (
          <Box sx={{ px: 1.5, py: 1.2 }}>
            <Typography variant="body2" sx={{ color: 'text.disabled', fontWeight: 400 }}>
              — pending —
            </Typography>
          </Box>
        ) : lines.map((l, i) => (
          <Box key={i} sx={{
            px: 1.5, py: 1,
            borderBottom: i === lines.length - 1 ? 'none' : '1px dashed',
            borderColor: (t) => alpha(t.palette[tone].main, 0.18),
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
          }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={800} fontSize={13} noWrap>
                {l.account || <Box component="span" sx={{ color: 'text.disabled', fontWeight: 400 }}>— pending —</Box>}
              </Typography>
              {l.kind && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {l.kind}
                </Typography>
              )}
            </Box>
            <Typography sx={{ ...TNUM, fontWeight: 800, fontSize: 14,
              color: l.account && l.amount > 0 ? `${tone}.dark` : 'text.disabled' }}>
              GHS {fmtMoney(l.amount > 0 ? l.amount : 0)}
            </Typography>
          </Box>
        ))}
        {/* Per-side total when multiple lines */}
        {lines.length > 1 && (
          <Box sx={{
            px: 1.5, py: 0.7,
            borderTop: '2px solid',
            borderColor: (t) => alpha(t.palette[tone].main, 0.4),
            bgcolor: (t) => alpha(t.palette[tone].main, 0.08),
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <Typography variant="caption" fontWeight={900}
              sx={{ letterSpacing: 1, fontSize: 10.5, color: `${tone}.main` }}>
              Σ {tone === 'primary' ? 'DR' : 'CR'}
            </Typography>
            <Typography sx={{ ...TNUM, fontWeight: 900, fontSize: 13.5, color: `${tone}.dark` }}>
              GHS {fmtMoney(lines.reduce((s, l) => s + (l.amount > 0 ? l.amount : 0), 0))}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ mt: 1 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.8 }}>
        <DoubleEntryIcon fontSize="small" sx={{ color: 'info.main' }} />
        <Typography variant="caption" fontWeight={800}
          sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 10.5, color: 'info.main' }}>
          Journal Preview
        </Typography>
        <Box sx={{ flex: 1 }} />
        {kind && (
          <Chip size="small" label={kind} variant="outlined"
            sx={{ height: 18, fontSize: 9.5, fontWeight: 800, letterSpacing: 0.5,
              borderColor: 'info.main', color: 'info.main' }} />
        )}
      </Stack>
      <Box sx={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0,
        border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
        overflow: 'hidden', bgcolor: 'background.paper',
      }}>
        {renderSide(drLines, 'primary', 'DEBIT (DR)')}
        {renderSide(crLines, 'success', 'CREDIT (CR)')}
      </Box>
      <Stack direction="row" alignItems="center" spacing={1}
        sx={{
          mt: 1, py: 0.7, px: 1.2, borderRadius: 1,
          bgcolor: balanced
            ? (t) => alpha(t.palette.success.main, 0.08)
            : (t) => alpha(t.palette.warning.main, 0.08),
          border: '1px solid',
          borderColor: balanced ? 'success.light' : 'warning.light',
        }}>
        <Box sx={{
          width: 8, height: 8, borderRadius: '50%',
          bgcolor: balanced ? 'success.main' : 'warning.main',
        }} />
        <Typography variant="caption" fontWeight={800}
          sx={{ color: balanced ? 'success.dark' : 'warning.dark' }}>
          {balanced
            ? `Balanced — Σ DR = Σ CR = GHS ${fmtMoney(headTotal)}`
            : ready
              ? `Σ items GHS ${fmtMoney(drTotal)} ≠ cheque total GHS ${fmtMoney(headTotal)}`
              : (hint || 'Fill in account + amount to preview the entry')}
        </Typography>
      </Stack>
    </Box>
  );
}

// ── Deposit dialog ─────────────────────────────────────────────
function DepositDialog({ open, onClose, accounts, defaultMonth, cashInHand, onSaved }) {
  const [form, setForm] = useState({ account: '', date: todayISO(), amount: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  useEffect(() => {
    if (open) {
      setForm({ account: '', date: todayISO(), amount: '' });
      setError('');
    }
  }, [open]);

  const submit = async () => {
    setSaving(true); setError('');
    try {
      await API.post('/cashbook/deposits', {
        account: form.account,
        date:    form.date,
        amount:  Number(form.amount),
      });
      onSaved && (await onSaved());
      onClose();
    } catch (e) { setError(e?.response?.data?.message || e.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const amtNum = Number(form.amount) || 0;
  const insufficient = amtNum > 0 && amtNum > Number(cashInHand || 0) + 0.001;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 2.5, overflow: 'hidden' } }}>
      {/* Header */}
      <Box sx={{
        background: (t) => `linear-gradient(135deg, ${t.palette.primary.dark} 0%, ${t.palette.primary.main} 60%, ${t.palette.info.main} 100%)`,
        color: '#fff', px: 3, py: 2.2, position: 'relative',
      }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 44, height: 44 }}>
            <DepositIcon />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="overline" sx={{ opacity: 0.85, letterSpacing: 1.2, fontWeight: 700, fontSize: 10 }}>
              Cashbook · New Entry
            </Typography>
            <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.15 }}>
              Cash → Bank Deposit
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
        </Stack>
        {/* Stat strip */}
        <Stack direction="row" spacing={2} sx={{ mt: 1.8 }}>
          <Box sx={{ flex: 1, p: 1.2, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.12)' }}>
            <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 700, fontSize: 10 }}>
              CASH-IN-HAND
            </Typography>
            <Typography fontWeight={800} sx={{ ...TNUM, fontSize: 15 }}>
              GHS {fmtMoney(cashInHand)}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, p: 1.2, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.12)' }}>
            <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 700, fontSize: 10 }}>
              MONTH
            </Typography>
            <Typography fontWeight={800} sx={{ fontSize: 15 }}>
              {monthYerOf(form.date)}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <DialogContent dividers sx={{ bgcolor: 'grey.50', p: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <Card variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2, bgcolor: 'background.paper' }}>
          <Typography variant="caption" fontWeight={800}
            sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 10.5, color: 'text.secondary', display: 'block', mb: 1 }}>
            Deposit Details
          </Typography>
          <Stack spacing={2}>
            <TextField select fullWidth size="small" label="Receiving Bank Account" required
              value={form.account}
              onChange={(e) => setForm({ ...form, account: e.target.value })}
              InputProps={{ startAdornment: <InputAdornment position="start"><BankIcon fontSize="small" sx={{ color: 'primary.main' }} /></InputAdornment> }}>
              <MenuItem value=""><em>— pick a bank account —</em></MenuItem>
              {accounts.filter(a => a.name !== CASH).map(a => (
                <MenuItem key={a.name} value={a.name}>
                  {a.name}
                  <Box component="span" sx={{ ml: 1, color: 'text.disabled', fontSize: 11 }}>
                    · {a.account_type || 'Bank'}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField fullWidth size="small" type="date" label="Deposit Date" required
                InputLabelProps={{ shrink: true }}
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })} />
              <TextField fullWidth size="small" type="number" label="Amount (GHS)" required
                value={form.amount}
                error={insufficient}
                helperText={insufficient ? `Exceeds cash-in-hand (GHS ${fmtMoney(cashInHand)})` : ' '}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                inputProps={{ style: { textAlign: 'right', ...TNUM, fontWeight: 800, fontSize: 16 } }} />
            </Stack>
          </Stack>
        </Card>

        <JournalPreview
          kind="DEPOSIT"
          dr={{ account: form.account, kind: 'Asset · Bank' }}
          cr={{ account: CASH, kind: 'Asset · Cash' }}
          amount={form.amount}
          hint="Pick a bank account and enter an amount."
        />

        <Alert severity="info" variant="outlined" sx={{ borderRadius: 1.5, mt: 2 }}>
          <Typography variant="caption" fontWeight={700}>
            Validation rules:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.2, fontSize: 12 }}>
            <li>Cash-in-hand must be ≥ deposit amount.</li>
            <li>The bank account must already have an opening balance for {monthYerOf(form.date)}.</li>
            <li>The date must fall inside the active accounting period.</li>
          </Box>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.7, bgcolor: 'background.paper' }}>
        <Button onClick={onClose} color="inherit" sx={{ fontWeight: 700, textTransform: 'none' }}>
          Cancel
        </Button>
        <Button variant="contained" color="primary"
          onClick={submit}
          disabled={saving || !form.account || !form.date || !(Number(form.amount) > 0) || insufficient}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          sx={{ fontWeight: 800, minWidth: 180, textTransform: 'none', boxShadow: 2 }}>
          {saving ? 'Posting…' : 'Post deposit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Bank → Bank transfer dialog ────────────────────────────────
//
// Moving the school's own money between its own accounts. Unlike a
// deposit or a payment this touches TWO accounts, so the dialog shows
// both live balances and what each will be afterwards — the whole risk
// with an internal transfer is picking the wrong side, and a number
// that visibly goes the wrong way is the cheapest way to catch it.
//
// No approval step: nothing leaves the school, so there is no
// expenditure for anyone to authorise.
function TransferDialog({ open, onClose, accounts, banks, onSaved }) {
  const [form, setForm] = useState({
    from_account: '', to_account: '', date: todayISO(),
    amount: '', reference: '', narration: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  useEffect(() => {
    if (open) {
      setForm({
        from_account: '', to_account: '', date: todayISO(),
        amount: '', reference: '', narration: '',
      });
      setError('');
    }
  }, [open]);

  // Live balances for the month the chosen date falls in, straight off
  // the Bank Position the page already loaded.
  const balOf = (name) => {
    const hit = (banks || []).find(b => b.account === name);
    return hit ? Number(hit.balance || 0) : null;
  };
  const fromBal = balOf(form.from_account);
  const toBal   = balOf(form.to_account);

  const amtNum = Number(form.amount) || 0;
  const sameAccount  = !!form.from_account && form.from_account === form.to_account;
  const insufficient = amtNum > 0 && fromBal !== null && amtNum > fromBal + 0.001;

  const submit = async () => {
    setSaving(true); setError('');
    try {
      await API.post('/cashbook/transfers', {
        from_account: form.from_account,
        to_account:   form.to_account,
        date:         form.date,
        amount:       Number(form.amount),
        reference:    form.reference,
        narration:    form.narration,
      });
      onSaved && (await onSaved());
      onClose();
    } catch (e) { setError(e?.response?.data?.message || e.message || 'Transfer failed'); }
    finally { setSaving(false); }
  };

  const bankList = (banks && banks.length)
    ? banks.map(b => ({ name: b.account, account_type: 'Bank' }))
    : (accounts || []).filter(a => a.name !== CASH);

  const SideCard = ({ label, account, before, after, tone }) => (
    <Box sx={{ flex: 1, p: 1.4, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.12)', minWidth: 0 }}>
      <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 700, fontSize: 10 }}>
        {label}
      </Typography>
      <Typography noWrap sx={{ fontSize: 12, fontWeight: 700, opacity: 0.95 }}>
        {account || '—'}
      </Typography>
      {before !== null && before !== undefined && (
        <Typography sx={{ ...TNUM, fontSize: 14, fontWeight: 800, mt: 0.3 }}>
          GHS {fmtMoney(before)}
          {amtNum > 0 && (
            <Box component="span" sx={{ fontSize: 11.5, opacity: 0.9, ml: 0.6 }}>
              → {fmtMoney(after)}
            </Box>
          )}
        </Typography>
      )}
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 2.5, overflow: 'hidden' } }}>
      <Box sx={{
        background: (t) => `linear-gradient(135deg, ${t.palette.primary.dark} 0%, ${t.palette.primary.main} 60%, ${t.palette.info.main} 100%)`,
        color: '#fff', px: 3, py: 2.2,
      }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 44, height: 44 }}>
            <TransferIcon />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="overline" sx={{ opacity: 0.85, letterSpacing: 1.2, fontWeight: 700, fontSize: 10 }}>
              Cashbook · New Entry
            </Typography>
            <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.15 }}>
              Bank → Bank Transfer
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
        </Stack>
        <Stack direction="row" spacing={1.5} sx={{ mt: 1.8 }} alignItems="center">
          <SideCard label="FROM" account={form.from_account}
            before={fromBal} after={(fromBal || 0) - amtNum} />
          <ArrowForwardIcon sx={{ opacity: 0.8 }} />
          <SideCard label="TO" account={form.to_account}
            before={toBal} after={(toBal || 0) + amtNum} />
        </Stack>
      </Box>

      <DialogContent dividers sx={{ bgcolor: 'grey.50', p: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <Card variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2, bgcolor: 'background.paper' }}>
          <Typography variant="caption" fontWeight={800}
            sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 10.5, color: 'text.secondary', display: 'block', mb: 1 }}>
            Transfer Details
          </Typography>
          <Stack spacing={2}>
            <TextField select fullWidth size="small" label="Transfer FROM" required
              value={form.from_account}
              onChange={(e) => setForm({ ...form, from_account: e.target.value })}
              InputProps={{ startAdornment: <InputAdornment position="start"><BankIcon fontSize="small" sx={{ color: 'error.main' }} /></InputAdornment> }}>
              <MenuItem value=""><em>— account the money leaves —</em></MenuItem>
              {bankList.map(a => (
                <MenuItem key={a.name} value={a.name}>
                  {a.name}
                  {balOf(a.name) !== null && (
                    <Box component="span" sx={{ ml: 1, color: 'text.disabled', fontSize: 11, ...TNUM }}>
                      · GHS {fmtMoney(balOf(a.name))}
                    </Box>
                  )}
                </MenuItem>
              ))}
            </TextField>

            <TextField select fullWidth size="small" label="Transfer TO" required
              value={form.to_account}
              error={sameAccount}
              helperText={sameAccount ? 'Pick a different account' : ' '}
              onChange={(e) => setForm({ ...form, to_account: e.target.value })}
              InputProps={{ startAdornment: <InputAdornment position="start"><BankIcon fontSize="small" sx={{ color: 'success.main' }} /></InputAdornment> }}>
              <MenuItem value=""><em>— account the money arrives in —</em></MenuItem>
              {bankList
                .filter(a => a.name !== form.from_account)
                .map(a => (
                  <MenuItem key={a.name} value={a.name}>
                    {a.name}
                    {balOf(a.name) !== null && (
                      <Box component="span" sx={{ ml: 1, color: 'text.disabled', fontSize: 11, ...TNUM }}>
                        · GHS {fmtMoney(balOf(a.name))}
                      </Box>
                    )}
                  </MenuItem>
                ))}
            </TextField>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField fullWidth size="small" type="date" label="Transfer Date" required
                InputLabelProps={{ shrink: true }}
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })} />
              <TextField fullWidth size="small" type="number" label="Amount (GHS)" required
                value={form.amount}
                error={insufficient}
                helperText={insufficient
                  ? `Exceeds ${form.from_account} balance (GHS ${fmtMoney(fromBal)})`
                  : ' '}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                inputProps={{ style: { textAlign: 'right', ...TNUM, fontWeight: 800, fontSize: 16 } }} />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField fullWidth size="small" label="Reference / cheque no."
                placeholder="e.g. transfer slip or cheque number"
                value={form.reference}
                onChange={(e) => setForm({ ...form, reference: e.target.value })} />
              <TextField fullWidth size="small" label="Narration"
                placeholder="why the money moved"
                value={form.narration}
                onChange={(e) => setForm({ ...form, narration: e.target.value })} />
            </Stack>
          </Stack>
        </Card>

        <JournalPreview
          kind="TRANSFER"
          dr={{ account: form.to_account,   kind: 'Asset · Bank' }}
          cr={{ account: form.from_account, kind: 'Asset · Bank' }}
          amount={form.amount}
          hint="Pick both accounts and enter an amount."
        />

        <Alert severity="info" variant="outlined" sx={{ borderRadius: 1.5, mt: 2 }}>
          <Typography variant="caption" fontWeight={700}>
            Validation rules:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.2, fontSize: 12 }}>
            <li>The two accounts must be different.</li>
            <li>The sending account must hold at least the transfer amount.</li>
            <li><b>Both</b> accounts need an opening balance for {monthYerOf(form.date)}.</li>
            <li>The date must fall inside the active accounting period.</li>
            <li>Total bank funds do not change — this is an internal move, not income or expenditure.</li>
            <li>The bank's own transfer charge is an expense — post it separately as a payment.</li>
          </Box>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.7, bgcolor: 'background.paper' }}>
        <Button onClick={onClose} color="inherit" sx={{ fontWeight: 700, textTransform: 'none' }}>
          Cancel
        </Button>
        <Button variant="contained" color="primary"
          onClick={submit}
          disabled={saving || !form.from_account || !form.to_account || sameAccount
            || !form.date || !(Number(form.amount) > 0) || insufficient}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          sx={{ fontWeight: 800, minWidth: 190, textTransform: 'none', boxShadow: 2 }}>
          {saving ? 'Posting…' : 'Post transfer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Payment voucher dialog ─────────────────────────────────────
// One cheque can pay for many things. The dialog supports a dynamic
// list of "items" — each row is one expenditure (category + type +
// amount + narration). Σ items must equal the cheque total before the
// "Post payment" button enables.
// `request` is an APPROVED expenditure request being turned into a
// cheque. When it is present the dialog is not really a form any more —
// what is being paid was signed off by up to three people, so the items,
// the amounts and the narrations are shown READ-ONLY and the spender
// supplies only the two things the request could not know: which account
// the money leaves, and the cheque number. Letting those fields stay
// editable would mean a signature could authorise one thing and a cheque
// pay for another.
function PaymentDialog({ open, onClose, accounts, categories, expTypes, editing, request, onSaved }) {
  const [form, setForm] = useState(blankPV());
  const [items, setItems] = useState([blankItem()]);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const [voucherNo, setVN]  = useState('');

  function blankPV() {
    return {
      account: '', date: todayISO(), amount: '',
      narration: '', cheque_no: '',
    };
  }
  function blankItem() {
    return { exp_cat: '', exp_ty: '', amount: '', narration: '', request_holder: '' };
  }

  useEffect(() => {
    if (open) {
      if (editing) {
        setForm({
          account:   editing.acc_name || '',
          date:      (editing.pay_date || '').slice(0, 10) || todayISO(),
          amount:    String(editing.amount || ''),
          narration: editing.narration || '',
          cheque_no: editing.cheque_no || '',
        });
        // Hydrate items from the voucher's saved breakdown; fall back
        // to the legacy single-line shape if items are absent.
        const ed = Array.isArray(editing.items) && editing.items.length > 0
          ? editing.items.map(it => ({
              exp_cat:        it.exp_cat || '',
              exp_ty:         it.exp_ty || '',
              amount:         String(it.amount || ''),
              narration:      it.narration || '',
              request_holder: it.request_holder || editing.request_holder || '',
            }))
          : [{
              exp_cat:        editing.exp_cat || '',
              exp_ty:         editing.exp_ty  || '',
              amount:         String(editing.amount || ''),
              narration:      editing.narration || '',
              request_holder: editing.request_holder || '',
            }];
        setItems(ed);
        setVN(editing.voucher_no || '');
      } else if (request) {
        // Straight off the approved request. The amount is the approved
        // total — the backend refuses a cheque that differs from it, so
        // showing anything else here would only set up a rejection.
        setForm({
          ...blankPV(),
          amount: String(request.total_amount || ''),
          narration: request.reason || '',
        });
        setItems((request.items || []).map(it => ({
          exp_cat:        it.exp_cat || '',
          exp_ty:         it.exp_ty || '',
          amount:         String(it.amount || ''),
          narration:      it.narration || '',
          request_holder: it.request_holder || '',
        })));
        setVN('');
        API.get('/cashbook/next-pv', { params: { date: todayISO() } })
          .then(r => setVN(r.data?.voucher_no || ''))
          .catch(() => {});
      } else {
        setForm(blankPV());
        setItems([blankItem()]);
        setVN('');
        API.get('/cashbook/next-pv', { params: { date: todayISO() } })
          .then(r => setVN(r.data?.voucher_no || ''))
          .catch(() => {});
      }
      setError('');
    }
  }, [open, editing, request]);

  useEffect(() => {
    if (!open || editing || !form.date) return;
    API.get('/cashbook/next-pv', { params: { date: form.date } })
      .then(r => setVN(r.data?.voucher_no || ''))
      .catch(() => {});
  }, [form.date, open, editing]);

  const updateItem = (i, patch) => {
    setItems(prev => prev.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  };
  const addItem    = () => setItems(prev => [...prev, blankItem()]);
  const removeItem = (i) => {
    setItems(prev => prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev);
  };

  // When there's exactly one item, auto-sync its amount to the cheque
  // total so the simple case "feels" like a single-amount form.
  useEffect(() => {
    if (items.length === 1) {
      const cheque = String(form.amount || '');
      if (items[0].amount !== cheque) {
        setItems([{ ...items[0], amount: cheque }]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.amount, items.length]);

  const itemsTotal = useMemo(
    () => items.reduce((s, it) => s + (Number(it.amount) || 0), 0),
    [items]
  );
  const cheque = Number(form.amount) || 0;
  const itemsBalanced = items.length > 0 && Math.abs(itemsTotal - cheque) < 0.01 && cheque > 0;

  const submit = async () => {
    setSaving(true); setError('');
    try {
      const payload = {
        account:   form.account,
        date:      form.date,
        amount:    Number(form.amount),
        narration: form.narration,
        cheque_no: form.cheque_no,
        // Which signed request this cheque pays. The backend refuses any
        // voucher without one while expenditure approval is on, and
        // marks the request PAID inside the same transaction as the
        // ledger writes so one signature can never fund two cheques.
        request_id: request ? request.id : undefined,
        items: items.map(it => ({
          exp_cat:        it.exp_cat,
          exp_ty:         it.exp_ty,
          amount:         Number(it.amount),
          narration:      it.narration,
          request_holder: it.request_holder,
        })),
      };
      const resp = editing
        ? await API.put(`/cashbook/payments/${editing.id}`, payload)
        : await API.post('/cashbook/payments', payload);
      onSaved && (await onSaved(resp.data));
      onClose();
    } catch (e) { setError(e?.response?.data?.message || e.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const isCash = form.account === CASH;
  // Per-item capital flag, computed once per category.
  const isCapitalCat = (catName) => {
    const c = categories.find(c => c.name === catName);
    return Number(c?.is_capital || 0) === 1;
  };
  const anyCapital = items.some(it => it.exp_cat && isCapitalCat(it.exp_cat));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: 2.5, overflow: 'hidden' } }}>
      {/* Header — same blue palette as the deposit dialog so the
          cashbook flows feel like one family */}
      <Box sx={{
        background: (t) => `linear-gradient(135deg, ${t.palette.primary.dark} 0%, ${t.palette.primary.main} 60%, ${t.palette.info.main} 100%)`,
        color: '#fff', px: 3, py: 2.2, position: 'relative',
      }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 44, height: 44 }}>
            <PaymentIcon />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="overline" sx={{ opacity: 0.85, letterSpacing: 1.2, fontWeight: 700, fontSize: 10 }}>
              {editing ? 'Cashbook · Edit Voucher' : 'Cashbook · New Payment'}
            </Typography>
            <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.15 }}>
              {editing ? `Edit ${editing.voucher_no}` : 'Payment Voucher'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
        </Stack>
        <Stack direction="row" spacing={2} sx={{ mt: 1.8 }}>
          <Box sx={{ flex: 1, p: 1.2, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.12)' }}>
            <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 700, fontSize: 10 }}>
              VOUCHER NO.
            </Typography>
            <Typography fontWeight={800} sx={{ fontFamily: FONT_MONO, fontSize: 13.5 }}>
              {voucherNo || '—'}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, p: 1.2, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.12)' }}>
            <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 700, fontSize: 10 }}>
              MONTH
            </Typography>
            <Typography fontWeight={800} sx={{ fontSize: 15 }}>
              {monthYerOf(form.date)}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, p: 1.2, borderRadius: 1.5,
            bgcolor: items.length > 1 ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.12)' }}>
            <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 700, fontSize: 10 }}>
              ITEMS
            </Typography>
            <Typography fontWeight={800} sx={{ fontSize: 15 }}>
              {items.length} {items.length === 1 ? 'line' : 'lines'}
              {anyCapital && (
                <Box component="span" sx={{ ml: 1, fontSize: 11, opacity: 0.9 }}>
                  · capital
                </Box>
              )}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <DialogContent dividers sx={{ bgcolor: 'grey.50', p: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* Payment From */}
        <Card variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2, bgcolor: 'background.paper' }}>
          <Typography variant="caption" fontWeight={800}
            sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 10.5, color: 'text.secondary', display: 'block', mb: 1 }}>
            Payment From
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
            <TextField select size="small" label="Payment Account" required
              value={form.account}
              onChange={(e) => setForm({ ...form, account: e.target.value })}
              InputProps={{ startAdornment: <InputAdornment position="start"><BankIcon fontSize="small" sx={{ color: 'error.main' }} /></InputAdornment> }}>
              <MenuItem value=""><em>— pick —</em></MenuItem>
              <MenuItem value={CASH}>
                <CashIcon fontSize="small" sx={{ mr: 0.7, color: 'success.main' }} /> Cash-In-Hand
              </MenuItem>
              {accounts.filter(a => a.name !== CASH).map(a => (
                <MenuItem key={a.name} value={a.name}>
                  {a.name}
                  <Box component="span" sx={{ ml: 1, color: 'text.disabled', fontSize: 11 }}>
                    · {a.account_type || 'Bank'}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
            <TextField size="small" type="date" label="Date" required
              InputLabelProps={{ shrink: true }}
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })} />
            <TextField size="small" type="number" label="Amount (GHS)" required
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              // Locked when paying an approved request: the amount was
              // signed for, and the backend rejects a cheque that
              // differs from it. An editable box here would only invite
              // a refusal after the spender had typed everything else.
              disabled={!!request}
              helperText={request ? `Approved on ${request.ref_no}` : undefined}
              inputProps={{ style: { textAlign: 'right', ...TNUM, fontWeight: 800, fontSize: 16 } }} />
            <TextField size="small" label="Cheque No"
              value={form.cheque_no}
              onChange={(e) => setForm({ ...form, cheque_no: e.target.value })}
              disabled={isCash}
              helperText={isCash ? 'Not required for cash' : 'Required for bank payments'}
              sx={{ gridColumn: { md: 'span 3' } }} />
          </Box>
        </Card>

        {/* Items — one cheque, many sub-lines */}
        <Card variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2, bgcolor: 'background.paper' }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="caption" fontWeight={800}
              sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 10.5, color: 'text.secondary' }}>
              Items Covered by This Cheque
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Chip size="small" variant="outlined"
              color={itemsBalanced ? 'success' : 'warning'}
              label={`Σ items: GHS ${fmtMoney(itemsTotal)} / ${fmtMoney(cheque)}`}
              sx={{ fontWeight: 800, fontSize: 11 }} />
          </Stack>

          {/* Compact single-line item rows in a scrollable container so
              the form never grows past ~360px tall regardless of how
              many items are in the cheque. */}
          <Box sx={{
            maxHeight: 360, overflowY: 'auto', pr: 0.5,
            mb: 1, mr: -0.5,
            // Custom thin scrollbar to keep the dialog feeling tidy
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: (t) => alpha(t.palette.text.disabled, 0.3),
              borderRadius: 3,
            },
          }}>
            {items.map((it, i) => {
              const types = expTypes.filter(t => !it.exp_cat || t.cat === it.exp_cat);
              const cap   = isCapitalCat(it.exp_cat);
              return (
                <Box key={i} sx={{
                  display: 'grid', gap: 1, mb: 1,
                  px: 1.2, py: 1.1, borderRadius: 1.5, bgcolor: 'grey.50',
                  border: '1px solid', borderColor: cap ? 'warning.light' : 'divider',
                  // Single-line layout on md+: # | Category | Type | Requester | Amount | Narration | Remove
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: '34px 1.4fr 1.4fr 1.1fr 110px 2fr 36px',
                  },
                  alignItems: 'center',
                }}>
                  <Avatar sx={{
                    width: 24, height: 24, fontSize: 11, fontWeight: 800,
                    bgcolor: cap ? 'warning.main' : 'primary.main',
                  }}>{i + 1}</Avatar>
                  {/* Every item field is locked when this cheque pays an
                      approved request. These lines are what the three
                      signatures were given for; if they could be edited
                      here, the signature would authorise one thing and
                      the cheque pay for another. */}
                  <TextField select size="small" label="Category" required
                    value={it.exp_cat} disabled={!!request}
                    onChange={(e) => updateItem(i, { exp_cat: e.target.value, exp_ty: '' })}>
                    <MenuItem value=""><em>— pick —</em></MenuItem>
                    {categories.map(c => (
                      <MenuItem key={c.name} value={c.name}>
                        {c.name}
                        {Number(c.is_capital) === 1 &&
                          <Chip size="small" color="warning" label="Cap"
                            sx={{ ml: 0.6, height: 16, fontSize: 9 }} />}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField select size="small" label="Type" required
                    value={it.exp_ty}
                    onChange={(e) => updateItem(i, { exp_ty: e.target.value })}
                    disabled={!it.exp_cat || !!request}>
                    <MenuItem value=""><em>— pick —</em></MenuItem>
                    {types.map(t => (
                      <MenuItem key={t.name} value={t.name}>{t.name}</MenuItem>
                    ))}
                  </TextField>
                  <TextField size="small" label="Request Holder" required
                    value={it.request_holder} disabled={!!request}
                    onChange={(e) => updateItem(i, { request_holder: e.target.value })} />
                  <TextField size="small" type="number" label="Amount" required
                    value={it.amount}
                    onChange={(e) => updateItem(i, { amount: e.target.value })}
                    inputProps={{ style: { textAlign: 'right', ...TNUM, fontWeight: 800 } }}
                    disabled={items.length === 1 || !!request} />
                  <TextField size="small" label="Item narration" required
                    value={it.narration} disabled={!!request}
                    onChange={(e) => updateItem(i, { narration: e.target.value })} />
                  <Tooltip title={request ? 'Approved lines cannot be removed'
                    : items.length === 1 ? 'Add another item below' : 'Remove this item'}>
                    <span>
                      <IconButton size="small"
                        onClick={() => removeItem(i)}
                        disabled={items.length === 1 || !!request}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              );
            })}
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <Button variant="outlined" size="small" startIcon={<AddIcon />}
              onClick={addItem} disabled={!!request}
              sx={{ textTransform: 'none', fontWeight: 700 }}>
              Add another item
            </Button>
            <Box sx={{ flex: 1 }} />
            {!itemsBalanced && cheque > 0 && (
              <Typography variant="caption" sx={{ color: 'warning.dark', fontWeight: 700 }}>
                {itemsTotal > cheque
                  ? `Over by GHS ${fmtMoney(itemsTotal - cheque)}`
                  : `Short by GHS ${fmtMoney(cheque - itemsTotal)}`}
              </Typography>
            )}
          </Stack>
        </Card>

        <Card variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2, bgcolor: 'background.paper' }}>
          <Typography variant="caption" fontWeight={800}
            sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 10.5, color: 'text.secondary', display: 'block', mb: 1 }}>
            Cheque Header (optional)
          </Typography>
          <TextField multiline minRows={2} size="small" fullWidth
            label="Cheque-level narration (optional)"
            value={form.narration}
            onChange={(e) => setForm({ ...form, narration: e.target.value })}
            helperText="Leave blank to use the items' own narrations as the cheque description." />
        </Card>

        <JournalPreview
          kind={anyCapital ? 'CAPITAL EXPENSE' : 'PAYMENT'}
          dr={items.map(it => ({
            account: it.exp_cat || '',
            kind: it.exp_ty ? `Expense · ${it.exp_ty}${isCapitalCat(it.exp_cat) ? ' (capitalised)' : ''}` : 'Expense',
            amount: Number(it.amount) || 0,
          }))}
          cr={{
            account: form.account || '',
            kind: isCash ? 'Asset · Cash' : 'Asset · Bank',
          }}
          amount={form.amount}
          hint="Pick account, fill in items, and they must sum to the cheque total."
        />

        <Alert severity="info" variant="outlined" sx={{ mt: 2, borderRadius: 1.5 }}>
          <Typography variant="caption" fontWeight={700}>Validation rules:</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.2, fontSize: 12 }}>
            <li>{isCash ? 'Cash B/F' : 'Bank opening balance'} must be set for {monthYerOf(form.date)}.</li>
            <li>{form.account || 'The chosen account'} must have enough balance for the cheque total.</li>
            <li>Σ items must equal the cheque total exactly.</li>
            <li>The date must fall inside the active accounting period & term.</li>
            {anyCapital && <li>Capital items also extend the asset register automatically.</li>}
          </Box>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.7, bgcolor: 'background.paper' }}>
        <Button onClick={onClose} color="inherit" sx={{ fontWeight: 700, textTransform: 'none' }}>
          Cancel
        </Button>
        <Button variant="contained" color="error"
          onClick={submit}
          disabled={
            saving
            || !form.account || !form.date || !(Number(form.amount) > 0)
            || (!isCash && !form.cheque_no)
            || !itemsBalanced
            || items.some(it =>
                 !it.exp_cat || !it.exp_ty
                 || !(Number(it.amount) > 0)
                 || !it.narration?.trim()
                 || !it.request_holder?.trim()
               )
          }
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          sx={{ fontWeight: 800, minWidth: 200, textTransform: 'none', boxShadow: 2 }}>
          {saving ? 'Posting…' : (editing ? 'Save changes' : 'Post payment')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Tiny SVG breakdown bar ─────────────────────────────────────
function BreakdownBar({ data, total }) {
  if (!data || data.length === 0) {
    return <Typography variant="caption" color="text.disabled">Nothing posted this month</Typography>;
  }
  const colors = ['#1565C0','#0d47a1','#2e7d32','#ed6c02','#d32f2f','#6a1b9a','#00838f','#ad1457','#5d4037','#455a64'];
  return (
    <Box>
      <Box sx={{
        height: 14, borderRadius: 999, overflow: 'hidden',
        display: 'flex', boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.08)',
      }}>
        {data.map((d, i) => (
          <Box key={i} title={`${d.label} · GHS ${fmtMoney(d.value)}`}
            sx={{
              width: `${(d.value / total) * 100}%`,
              bgcolor: colors[i % colors.length],
              transition: 'width .25s',
            }} />
        ))}
      </Box>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.6} rowGap={0.6} sx={{ mt: 1 }}>
        {data.map((d, i) => (
          <Chip key={i} size="small"
            sx={{
              bgcolor: alpha(colors[i % colors.length], 0.12),
              color: colors[i % colors.length],
              border: `1px solid ${alpha(colors[i % colors.length], 0.3)}`,
              fontWeight: 700,
            }}
            label={`${d.label} · GHS ${fmtMoney(d.value)} · ${((d.value / total) * 100).toFixed(0)}%`} />
        ))}
      </Stack>
    </Box>
  );
}

// ── Double-entry breakdown dialog ──────────────────────────────
// Fetches journal_entries for one transaction by uuid and renders
// the DR / CR pair as a classic accounting T-account so the user
// can see exactly which two accounts were touched and by how much.
function DoubleEntryDialog({ open, onClose, uuid, label }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => {
    if (!open || !uuid) return;
    setLoading(true); setError(''); setData(null);
    API.get(`/cashbook/journal-entry/${uuid}`)
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.data?.message || 'Could not load journal entries'))
      .finally(() => setLoading(false));
  }, [open, uuid]);

  const drLines = (data?.lines || []).filter(l => l.side === 'DR');
  const crLines = (data?.lines || []).filter(l => l.side === 'CR');
  const totalDr = drLines.reduce((s, l) => s + Number(l.amount || 0), 0);
  const totalCr = crLines.reduce((s, l) => s + Number(l.amount || 0), 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
      <Box sx={{
        background: (t) => `linear-gradient(135deg, ${t.palette.info.main} 0%, ${t.palette.primary.main} 100%)`,
        color: '#fff', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5,
      }}>
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.22)' }}><DoubleEntryIcon /></Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={800}>Double-Entry View</Typography>
          <Typography variant="caption" sx={{ opacity: 0.92, fontFamily: FONT_MONO }} noWrap>
            {label || uuid}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
      </Box>

      <DialogContent dividers sx={{ bgcolor: 'grey.50', p: 3 }}>
        {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}
        {error && <Alert severity="error">{error}</Alert>}
        {!loading && !data?.transaction && !error && (
          <Alert severity="info" variant="outlined">
            No journal entry was recorded for this transaction. Older receipts may
            not yet have a journal mirror — re-run the backfill if needed.
          </Alert>
        )}
        {!loading && data?.transaction && (
          <>
            {/* Header strip — kind / date / ref / total */}
            <Box sx={{
              display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr 1fr' },
              gap: 1, mb: 2, p: 1.5, bgcolor: 'background.paper', borderRadius: 1.5,
              border: 1, borderColor: 'divider',
            }}>
              <Stat label="Kind"     value={String(data.transaction.kind || '').toUpperCase()} mono />
              <Stat label="Date"     value={fmtDate(data.transaction.txn_date)} />
              <Stat label="Reference" value={data.transaction.reference_no || '—'} mono />
              <Stat label="Total"    value={`GHS ${fmtMoney(data.transaction.total_amount)}`} highlight />
            </Box>

            {/* T-form: DR | CR with shared divider line */}
            <Box sx={{ mb: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.8 }}>
                <DoubleEntryIcon fontSize="small" sx={{ color: 'info.main' }} />
                <Typography variant="caption" fontWeight={800}
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 10.5, color: 'info.main' }}>
                  Double-Entry · T-Account View
                </Typography>
              </Stack>
              <Box sx={{
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0,
                border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
                overflow: 'hidden', bgcolor: 'background.paper',
              }}>
                {/* DR side */}
                <Box sx={{
                  borderRight: '1px solid', borderColor: 'divider',
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                }}>
                  <Box sx={{
                    px: 2, py: 0.8,
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.10),
                    borderBottom: '1px solid', borderColor: 'divider',
                    textAlign: 'center',
                  }}>
                    <Typography variant="caption" fontWeight={800}
                      sx={{ letterSpacing: 1.2, fontSize: 11, color: 'primary.main' }}>
                      DEBIT  (DR)
                    </Typography>
                  </Box>
                  {drLines.length === 0 && (
                    <Box sx={{ px: 2, py: 2, color: 'text.disabled', fontStyle: 'italic', textAlign: 'center' }}>
                      <Typography variant="body2">No debit lines</Typography>
                    </Box>
                  )}
                  {drLines.map((l, i) => (
                    <Box key={i} sx={{
                      px: 2, py: 1.1,
                      borderBottom: i === drLines.length - 1 ? 'none' : '1px dashed',
                      borderColor: (t) => alpha(t.palette.primary.main, 0.18),
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
                    }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={800} fontSize={13} noWrap>
                          {l.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary"
                          sx={{ fontFamily: FONT_MONO, fontSize: 10 }}>
                          {l.code} · {l.type}
                        </Typography>
                      </Box>
                      <Typography sx={{ ...TNUM, fontWeight: 800, fontSize: 14, color: 'primary.dark' }}>
                        GHS {fmtMoney(l.amount)}
                      </Typography>
                    </Box>
                  ))}
                  <Box sx={{
                    px: 2, py: 1, borderTop: '2px solid',
                    borderColor: (t) => alpha(t.palette.primary.main, 0.4),
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.10),
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <Typography variant="caption" fontWeight={900}
                      sx={{ letterSpacing: 1, fontSize: 11, color: 'primary.main' }}>
                      Σ DR
                    </Typography>
                    <Typography sx={{ ...TNUM, fontWeight: 900, fontSize: 14.5, color: 'primary.dark' }}>
                      GHS {fmtMoney(totalDr)}
                    </Typography>
                  </Box>
                </Box>

                {/* CR side */}
                <Box sx={{ bgcolor: (t) => alpha(t.palette.success.main, 0.04) }}>
                  <Box sx={{
                    px: 2, py: 0.8,
                    bgcolor: (t) => alpha(t.palette.success.main, 0.10),
                    borderBottom: '1px solid', borderColor: 'divider',
                    textAlign: 'center',
                  }}>
                    <Typography variant="caption" fontWeight={800}
                      sx={{ letterSpacing: 1.2, fontSize: 11, color: 'success.main' }}>
                      CREDIT  (CR)
                    </Typography>
                  </Box>
                  {crLines.length === 0 && (
                    <Box sx={{ px: 2, py: 2, color: 'text.disabled', fontStyle: 'italic', textAlign: 'center' }}>
                      <Typography variant="body2">No credit lines</Typography>
                    </Box>
                  )}
                  {crLines.map((l, i) => (
                    <Box key={i} sx={{
                      px: 2, py: 1.1,
                      borderBottom: i === crLines.length - 1 ? 'none' : '1px dashed',
                      borderColor: (t) => alpha(t.palette.success.main, 0.18),
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
                    }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={800} fontSize={13} noWrap>
                          {l.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary"
                          sx={{ fontFamily: FONT_MONO, fontSize: 10 }}>
                          {l.code} · {l.type}
                        </Typography>
                      </Box>
                      <Typography sx={{ ...TNUM, fontWeight: 800, fontSize: 14, color: 'success.dark' }}>
                        GHS {fmtMoney(l.amount)}
                      </Typography>
                    </Box>
                  ))}
                  <Box sx={{
                    px: 2, py: 1, borderTop: '2px solid',
                    borderColor: (t) => alpha(t.palette.success.main, 0.4),
                    bgcolor: (t) => alpha(t.palette.success.main, 0.10),
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <Typography variant="caption" fontWeight={900}
                      sx={{ letterSpacing: 1, fontSize: 11, color: 'success.main' }}>
                      Σ CR
                    </Typography>
                    <Typography sx={{ ...TNUM, fontWeight: 900, fontSize: 14.5, color: 'success.dark' }}>
                      GHS {fmtMoney(totalCr)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Balanced status pill */}
            <Stack direction="row" alignItems="center" spacing={1}
              sx={{
                mt: 1.5, py: 0.85, px: 1.4, borderRadius: 1,
                bgcolor: Math.abs(totalDr - totalCr) < 0.01
                  ? (t) => alpha(t.palette.success.main, 0.08)
                  : (t) => alpha(t.palette.error.main, 0.08),
                border: '1px solid',
                borderColor: Math.abs(totalDr - totalCr) < 0.01 ? 'success.light' : 'error.light',
              }}>
              <Box sx={{
                width: 8, height: 8, borderRadius: '50%',
                bgcolor: Math.abs(totalDr - totalCr) < 0.01 ? 'success.main' : 'error.main',
              }} />
              <Typography variant="caption" fontWeight={800}
                sx={{ color: Math.abs(totalDr - totalCr) < 0.01 ? 'success.dark' : 'error.dark' }}>
                {Math.abs(totalDr - totalCr) < 0.01
                  ? `Balanced — Σ DR = Σ CR = GHS ${fmtMoney(totalDr)}`
                  : `Imbalance: GHS ${fmtMoney(Math.abs(totalDr - totalCr))} (${totalDr > totalCr ? 'DR exceeds CR' : 'CR exceeds DR'})`}
              </Typography>
            </Stack>

            {data.transaction.memo && (
              <Box sx={{ mt: 2, p: 1.5, borderRadius: 1.5, bgcolor: 'background.paper',
                border: 1, borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 10 }}>
                  Memo
                </Typography>
                <Typography variant="body2">{data.transaction.memo}</Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.7 }}>
        <Button onClick={onClose} variant="contained" color="primary" sx={{ fontWeight: 800, textTransform: 'none' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── LedgerCard: cashbook-form-style table wrapper ──────────────
// Header strip with title + subtitle + count + search field, then
// the children (the actual table + pagination) sit below.
function LedgerCard({ title, subtitle, icon, tone = 'primary', count,
                     search, onSearch, placeholder,
                     dateFrom, dateTo, onDateFrom, onDateTo,
                     children }) {
  const hasDateFilter = onDateFrom && onDateTo;
  const hasActiveDateFilter = !!(dateFrom || dateTo);
  return (
    <Card sx={{
      borderRadius: 2, overflow: 'hidden',
      border: '1px solid',
      borderColor: (t) => alpha(t.palette[tone].main, 0.25),
      boxShadow: (t) => `0 2px 8px ${alpha(t.palette[tone].main, 0.06)}`,
    }}>
      {/* Title row */}
      <Box sx={{
        px: 2.5, py: 1.4,
        background: (t) => `linear-gradient(135deg, ${alpha(t.palette[tone].main, 0.08)} 0%, ${alpha(t.palette[tone].main, 0.02)} 100%)`,
        borderBottom: 1, borderColor: 'divider',
        display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
      }}>
        <Avatar sx={{ width: 30, height: 30, bgcolor: `${tone}.main`, fontSize: 14 }}>
          {icon}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ lineHeight: 1.1 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
          )}
        </Box>
        <Chip size="small" variant="outlined"
          label={`${count} row${count === 1 ? '' : 's'}`}
          sx={{ fontWeight: 700, bgcolor: 'background.paper' }} />
        <TextField size="small" placeholder={placeholder || 'Search…'}
          value={search} onChange={(e) => onSearch(e.target.value)}
          sx={{ minWidth: 220, bgcolor: 'background.paper', borderRadius: 1 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            endAdornment: search && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => onSearch('')}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }} />
      </Box>
      {/* Date filter row */}
      {hasDateFilter && (
        <Box sx={{
          px: 2.5, py: 1.1,
          bgcolor: 'background.paper',
          borderBottom: 1, borderColor: 'divider',
          display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap',
        }}>
          <Typography variant="caption" fontWeight={800}
            sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 10, color: 'text.secondary' }}>
            Date range
          </Typography>
          <TextField size="small" type="date" label="From"
            InputLabelProps={{ shrink: true }}
            value={dateFrom || ''}
            onChange={(e) => onDateFrom(e.target.value)}
            sx={{ width: 170 }} />
          <TextField size="small" type="date" label="To"
            InputLabelProps={{ shrink: true }}
            value={dateTo || ''}
            onChange={(e) => onDateTo(e.target.value)}
            sx={{ width: 170 }} />
          {hasActiveDateFilter && (
            <Button size="small" variant="text" color="inherit"
              onClick={() => { onDateFrom(''); onDateTo(''); }}
              startIcon={<CloseIcon fontSize="small" />}
              sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12 }}>
              Clear dates
            </Button>
          )}
        </Box>
      )}
      {children}
    </Card>
  );
}

function Stat({ label, value, mono, highlight }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 10, display: 'block' }}>
        {label}
      </Typography>
      <Typography sx={{
        fontFamily: mono ? FONT_MONO : FONT_ACCT,
        fontWeight: 800, fontSize: 14,
        color: highlight ? 'error.main' : 'text.primary',
        ...TNUM,
      }} noWrap>
        {value}
      </Typography>
    </Box>
  );
}

// ── Main page ──────────────────────────────────────────────────
export default function CashbookPage() {
  const isMobile = useIsMobile();
  const [month, setMonth] = useState(monthYerOf(todayISO()));
  const [months, setMonths] = useState([]);
  const [snap, setSnap]     = useState(null);
  const [loading, setLoading] = useState(true);

  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [expTypes, setExpTypes] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [school, setSchool]     = useState(null);

  const [accountFilter, setAccountFilter] = useState('');
  const [depOpen, setDepOpen] = useState(false);
  const [xferOpen, setXferOpen] = useState(false);
  const [transfers, setTransfers] = useState([]);
  const [pvOpen,  setPvOpen]  = useState(false);
  const [pvEditing, setPvEditing] = useState(null);
  // The approved expenditure request being turned into a cheque, when
  // the voucher dialog was opened from "Ready to pay" rather than from
  // the New Payment button.
  const [pvRequest, setPvRequest] = useState(null);
  const [reqRefresh, setReqRefresh] = useState(0);

  // Search + pagination per table (kept independent so each can scroll alone)
  const [voucherQ,    setVoucherQ]    = useState('');
  const [voucherFrom, setVoucherFrom] = useState('');
  const [voucherTo,   setVoucherTo]   = useState('');
  const [voucherPage, setVoucherPage] = useState(0);
  const [voucherRpp,  setVoucherRpp]  = useState(10);
  const [journalQ,    setJournalQ]    = useState('');
  const [journalFrom, setJournalFrom] = useState('');
  const [journalTo,   setJournalTo]   = useState('');
  const [journalPage, setJournalPage] = useState(0);
  const [journalRpp,  setJournalRpp]  = useState(10);

  // Double-entry breakdown dialog
  const [deOpen, setDeOpen] = useState(false);
  const [deUuid, setDeUuid] = useState('');
  const [deLabel, setDeLabel] = useState('');
  const openDoubleEntry = (uuid, label) => {
    setDeUuid(uuid); setDeLabel(label || ''); setDeOpen(true);
  };

  // Cashbook approval flag (read-only here; super_admins flip it
  // from the Settings page).
  const [approvalRequired, setApprovalRequired] = useState(false);
  const [canApprove, setCanApprove] = useState(false);
  const loadCashbookSettings = async () => {
    try {
      const r = await API.get('/cashbook/settings');
      setApprovalRequired(!!r.data?.expenditure_approval_required);
      setCanApprove(!!r.data?.can_approve_payments);
    } catch { /* silent */ }
  };
  useEffect(() => { loadCashbookSettings(); }, []);

  const loadRefs = async () => {
    try {
      const [bAcc, cats, types, info] = await Promise.all([
        API.get('/payments/bank-accounts'),
        API.get('/expenditures/categories'),
        API.get('/expenditures', { params: { perPage: 'all' } }),
        API.get('/setup/info').catch(() => ({ data: null })),
      ]);
      setAccounts(bAcc.data || []);
      setCategories(cats.data || []);
      setExpTypes(types.data?.rows || []);
      // /setup/info returns { success, info: { school_name, motto, … } }
      setSchool(info.data?.info || info.data || null);
    } catch { /* silent */ }
  };

  const loadMonths = async () => {
    try {
      const r = await API.get('/cashbook/months');
      setMonths(r.data || []);
      // If the active month doesn't exist yet, keep what the user typed.
    } catch { setMonths([]); }
  };

  const loadSnapshot = async () => {
    setLoading(true);
    try {
      const r = await API.get('/cashbook/snapshot', {
        params: { month, account: accountFilter },
      });
      setSnap(r.data);
    } catch (e) { swal.error(swal.pickError(e, 'Could not load cashbook')); }
    finally { setLoading(false); }
  };

  const loadVouchers = async () => {
    try {
      const r = await API.get('/cashbook/vouchers', { params: { month } });
      setVouchers(r.data || []);
    } catch { setVouchers([]); }
  };

  const loadTransfers = async () => {
    try {
      const r = await API.get('/cashbook/transfers', { params: { mon_yer: month } });
      setTransfers(r.data || []);
    } catch { setTransfers([]); }
  };

  useEffect(() => { loadRefs(); loadMonths(); }, []);
  useEffect(() => {
    loadSnapshot(); loadVouchers(); loadTransfers();
    /* eslint-disable-next-line */
  }, [month, accountFilter]);

  // ── Actions ──
  const bringForwardCash = async () => {
    const ok = await swal.confirm({
      title: `Bring forward Cash B/F for ${month}?`,
      text: 'Carries the previous month\'s closing cash position into this month. Idempotent — safe to click once.',
      tone: 'primary', confirmText: 'Yes, bring forward',
    });
    if (!ok) return;
    try {
      await API.post('/cashbook/bring-forward/cash', { month });
      swal.success('Cash B/F set');
      loadSnapshot();
    } catch (e) { swal.error(swal.pickError(e, 'Bring forward failed')); }
  };
  const bringForwardBank = async (account) => {
    const ok = await swal.confirm({
      title: `Bring forward ${account} for ${month}?`,
      text: 'Carries the previous month\'s closing balance into this month.',
      tone: 'primary', confirmText: 'Yes, bring forward',
    });
    if (!ok) return;
    try {
      await API.post('/cashbook/bring-forward/bank', { month, account });
      swal.success(`${account} B/F set`);
      loadSnapshot();
    } catch (e) { swal.error(swal.pickError(e, 'Bring forward failed')); }
  };
  const recomputeAll = async () => {
    const ok = await swal.confirm({
      title: 'Recompute brought-forward across all months?',
      text: 'Walks every month in date order and re-stamps each B/F row from the previous month\'s closing balance. Use after a back-dated edit.',
      tone: 'warning', confirmText: 'Yes, recompute',
    });
    if (!ok) return;
    try {
      const r = await API.post('/cashbook/recompute-bf');
      swal.success(`Updated ${r.data?.updated?.length || 0} row(s)`);
      loadSnapshot();
    } catch (e) { swal.error(swal.pickError(e, 'Recompute failed')); }
  };
  // Reverse a Cash → Bank deposit row (from the Running Journal).
  const reverseDeposit = async (r) => {
    const reason = await swal.prompt({
      title: `Reverse deposit?`,
      text: `Cash → ${r.account} · GHS ${fmtMoney(r.deposit)} on ${r.date}`,
      inputLabel: 'Reason',
      inputType: 'textarea',
      confirmText: 'Reverse',
      validate: (val) => (!val || val.trim().length < 5) ? 'Reason ≥ 5 chars' : null,
    });
    if (!reason) return;
    try {
      await API.delete(`/cashbook/deposits/${r.id}`, { data: { reason } });
      swal.success('Deposit reversed');
      loadSnapshot();
    } catch (e) { swal.error(swal.pickError(e, 'Reverse failed')); }
  };

  // Reverse a Bank → Bank transfer. Either leg can be clicked — the
  // server undoes both, because the two rows share one transaction id.
  const reverseTransfer = async (r) => {
    const counterparty = String(r.category || '').replace(/^(To|From):\s*/i, '');
    const outgoing = Number(r.payment || 0) > 0;
    const amount = outgoing ? r.payment : r.deposit;
    const reason = await swal.prompt({
      title: 'Reverse transfer?',
      text: outgoing
        ? `${r.account} → ${counterparty} · GHS ${fmtMoney(amount)} on ${r.date}`
        : `${counterparty} → ${r.account} · GHS ${fmtMoney(amount)} on ${r.date}`,
      inputLabel: 'Reason',
      inputType: 'textarea',
      confirmText: 'Reverse',
      validate: (val) => (!val || val.trim().length < 5) ? 'Reason ≥ 5 chars' : null,
    });
    if (!reason) return;
    try {
      await API.delete(`/cashbook/transfers/${r.id}`, { data: { reason } });
      swal.success('Transfer reversed — both accounts restored');
      loadSnapshot(); loadTransfers();
    } catch (e) { swal.error(swal.pickError(e, 'Reverse failed')); }
  };

  // Reverse from the Bank Transfers table, where a row is the WHOLE
  // transfer rather than one leg. Spells out both accounts and what
  // each will go back to, because this is the last screen before the
  // money moves again.
  const reverseTransferRow = async (x) => {
    const reason = await swal.prompt({
      title: 'Reverse this transfer?',
      text: `GHS ${fmtMoney(x.amount)} will move back from ${x.to_account} `
        + `to ${x.from_account}.\nDated ${x.date}`
        + `${x.reference ? ` · ref ${x.reference}` : ''}.`,
      inputLabel: 'Reason',
      inputType: 'textarea',
      confirmText: 'Reverse transfer',
      validate: (val) => (!val || val.trim().length < 5) ? 'Reason ≥ 5 chars' : null,
    });
    if (!reason) return;
    try {
      await API.delete(`/cashbook/transfers/${x.out_trans_code || x.in_trans_code}`,
        { data: { reason } });
      swal.success(`Reversed — GHS ${fmtMoney(x.amount)} back in ${x.from_account}`);
      loadSnapshot(); loadTransfers();
    } catch (e) { swal.error(swal.pickError(e, 'Reverse failed')); }
  };

  const removeVoucher = async (v) => {
    const reason = await swal.prompt({
      title: `Reverse ${v.voucher_no}?`,
      text: `${v.exp_ty} · ${v.acc_name} · GHS ${fmtMoney(v.amount)}`,
      inputLabel: 'Reason',
      inputType: 'textarea',
      confirmText: 'Reverse',
      validate: (val) => (!val || val.trim().length < 5) ? 'Reason ≥ 5 chars' : null,
    });
    if (!reason) return;
    try {
      await API.delete(`/cashbook/payments/${v.id}`, { data: { reason } });
      swal.success('Reversed');
      loadSnapshot(); loadVouchers();
    } catch (e) { swal.error(swal.pickError(e, 'Reverse failed')); }
  };
  // ── Expenditure Approval Form (paper-form style) ──────────────
  // Mirrors the Jehovah Shammah School expenditure approval form:
  // school header, requisitioner info, items table, expenditure
  // category checkboxes, payee/cheque info, three-tier approval
  // signature row (Department Head, 1st Level, Final Level), payment
  // mode boxes, and a recipient signature line.
  const printApprovalForm = (v) => {
    const w = window.open('', '_blank', 'width=920,height=1100');
    if (!w) return;
    const items = (v.items && v.items.length > 0)
      ? v.items
      : [{
          exp_cat: v.exp_cat, exp_ty: v.exp_ty,
          amount: Number(v.amount) || 0,
          narration: v.narration || '',
          request_holder: v.request_holder || '',
        }];
    const total = items.reduce((s, it) => s + Number(it.amount || 0), 0);
    const blanks = Math.max(0, 10 - items.length);
    const isCash = v.acc_name === CASH;
    const isPosted   = v.status === 'POSTED';
    const isPending  = v.status === 'PENDING';
    const isRejected = v.status === 'REJECTED';

    const esc = (s) => String(s ?? '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

    // Convert a numeric amount into words, e.g. 6825 → "Six Thousand Eight Hundred Twenty-Five Cedis Only"
    const numToWords = (n) => {
      const a = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten',
                 'Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
      const b = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
      const inWords = (num) => {
        if (num === 0) return 'Zero';
        const conv = (m) => {
          let s = '';
          if (m >= 100) { s += a[Math.floor(m/100)] + ' Hundred '; m %= 100; }
          if (m >= 20) { s += b[Math.floor(m/10)]; m %= 10; if (m) s += '-' + a[m]; }
          else if (m > 0) s += a[m];
          return s.trim();
        };
        const parts = [];
        const billion = Math.floor(num / 1e9); num %= 1e9;
        const million = Math.floor(num / 1e6); num %= 1e6;
        const thousand = Math.floor(num / 1e3); num %= 1e3;
        if (billion) parts.push(conv(billion) + ' Billion');
        if (million) parts.push(conv(million) + ' Million');
        if (thousand) parts.push(conv(thousand) + ' Thousand');
        if (num) parts.push(conv(num));
        return parts.join(' ').trim();
      };
      const cedis = Math.floor(n);
      const pesewas = Math.round((n - cedis) * 100);
      let out = inWords(cedis) + ' Cedis';
      if (pesewas > 0) out += ' and ' + inWords(pesewas) + ' Pesewas';
      out += ' Only';
      return out;
    };

    const sname    = esc(school?.school_name || 'School');
    const slogo    = school?.logo || '';
    const dateSubmitted = fmtDate(v.created_at || v.pay_date);
    const dateNeeded    = fmtDate(v.pay_date);

    // Aggregate the highest-level "expenditure type" classification
    // for the bottom checkboxes. We mark whichever item categories
    // match the headers below; if none match, none are ticked.
    const hasCat = (kw) => items.some(it => (it.exp_cat || '').toLowerCase().includes(kw));
    const checkbox = (checked) =>
      `<span class="cb">${checked ? '✓' : ''}</span>`;

    const itemRows = [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      itemRows.push(`
        <tr>
          <td class="num">${i + 1}</td>
          <td>${esc(it.exp_ty)}${it.narration ? `<div class="sub">${esc(it.narration)}</div>` : ''}</td>
          <td class="qty">1</td>
          <td>${esc(it.exp_cat || '')}</td>
          <td class="right">${fmtMoney(it.amount)}</td>
          <td class="right">${fmtMoney(it.amount)}</td>
        </tr>`);
    }
    for (let i = 0; i < blanks; i++) {
      itemRows.push(`<tr><td class="num">${items.length + i + 1}</td><td></td><td></td><td></td><td></td><td></td></tr>`);
    }

    const html = `<!doctype html><html><head><title>Approval Form · ${esc(v.voucher_no)}</title>
      <style>
        @page { size: A4; margin: 10mm; }
        * { box-sizing: border-box; }
        body {
          font-family: 'IBM Plex Sans', Inter, Arial, sans-serif;
          margin: 0; padding: 14px; color: #000; font-size: 11px;
          line-height: 1.3;
        }
        .head {
          display: flex; align-items: center; gap: 12px;
          padding-bottom: 8px;
        }
        .head img.logo { width: 56px; height: 56px; object-fit: contain; }
        .head .name { flex: 1; text-align: center; }
        .head h1 {
          margin: 0; font-size: 20px; font-weight: 800;
          letter-spacing: 2px; text-transform: uppercase;
        }
        .head h2 {
          margin: 2px 0 0; font-size: 13px; letter-spacing: 4px;
          text-transform: uppercase; font-weight: 700;
        }
        .status {
          position: absolute; right: 18px; top: 18px;
          font-size: 10px; font-weight: 800; letter-spacing: 1.5px;
          padding: 4px 10px; border-radius: 4px;
        }
        .status.posted   { background: #dcfce7; color: #166534; border: 1px solid #166534; }
        .status.pending  { background: #fef3c7; color: #92400e; border: 1px solid #92400e; }
        .status.rejected { background: #fee2e2; color: #991b1b; border: 1px solid #991b1b; }

        table { border-collapse: collapse; width: 100%; }
        td, th { border: 1px solid #000; padding: 4px 6px; vertical-align: top; }
        th {
          background: #f8fafc; text-transform: uppercase; font-size: 9.5px;
          letter-spacing: 0.4px; font-weight: 700;
        }
        .lbl {
          font-size: 9.5px; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.4px;
        }
        .field {
          display: flex; align-items: baseline; gap: 4px;
          padding: 3px 6px; border: 1px solid #000;
        }
        .field .v { flex: 1; border-bottom: 0; font-weight: 600; min-height: 14px; }

        /* Identification row */
        .id-grid {
          display: grid; grid-template-columns: 1.4fr 1fr 1fr 0.7fr;
          gap: 0; margin-top: 6px;
        }
        .id-grid > div {
          border: 1px solid #000; padding: 4px 6px;
          margin-left: -1px; margin-top: -1px;
        }
        .id-grid > div:first-child { margin-left: 0; }

        .info-section {
          margin-top: 8px; padding: 6px;
          border: 2px solid #000; border-radius: 0;
        }
        .info-section .title {
          font-weight: 800; text-transform: uppercase;
          font-size: 11px; letter-spacing: 1px;
          margin-bottom: 4px; text-align: center;
          background: #000; color: #fff; padding: 3px 8px;
        }

        .info-fields {
          display: grid; grid-template-columns: 1fr 1fr;
          gap: 0;
        }
        .info-fields .row {
          padding: 3px 6px; border: 1px solid #000;
          margin-left: -1px; margin-top: -1px;
        }
        .info-fields .row:first-child,
        .info-fields .row:nth-child(2) { margin-top: 0; }
        .info-fields .row:nth-child(odd) { margin-left: 0; }

        table.items {
          margin-top: 6px;
          font-size: 11px;
        }
        table.items td.num { width: 22px; text-align: center; font-weight: 700; }
        table.items td.qty { width: 60px; text-align: center; }
        table.items td.right { text-align: right; font-variant-numeric: tabular-nums; font-weight: 600; }
        table.items th.right { text-align: right; }
        table.items tr.total td {
          background: #000; color: #fff;
          font-weight: 800; text-align: right;
          letter-spacing: 0.5px;
        }

        .check-row {
          margin-top: 6px; display: grid;
          grid-template-columns: repeat(4, 1fr);
          border: 1px solid #000;
        }
        .check-row > div {
          padding: 5px 8px; border-right: 1px solid #000;
          display: flex; align-items: center; gap: 6px;
          font-size: 10px;
        }
        .check-row > div:last-child { border-right: none; }
        .cb {
          display: inline-block; width: 14px; height: 14px;
          border: 1.4px solid #000; text-align: center;
          line-height: 12px; font-weight: 800; font-size: 12px;
        }

        .credit-row {
          margin-top: 6px; padding: 4px 6px;
          border: 1px solid #000; font-size: 10.5px;
          display: flex; align-items: center; gap: 12px;
        }
        .credit-row .opt { display: flex; align-items: center; gap: 4px; }

        .words {
          margin-top: 6px; padding: 5px 8px;
          border: 1px solid #000;
        }
        .words .lbl { display: block; }
        .words .v {
          font-style: italic; font-weight: 700;
          padding: 2px 0; min-height: 14px; font-size: 11px;
        }

        .payee {
          margin-top: 6px; padding: 5px 8px;
          border: 1px solid #000;
        }

        .approval-grid {
          margin-top: 8px;
          display: grid; grid-template-columns: 1fr 1fr 1fr;
          border: 1px solid #000;
        }
        .approval-grid > div {
          padding: 4px 6px; border-right: 1px solid #000;
        }
        .approval-grid > div:last-child { border-right: none; }
        .approval-grid .col-title {
          text-align: center; font-weight: 800;
          text-transform: uppercase; font-size: 9.5px;
          letter-spacing: 0.6px; padding-bottom: 3px;
          border-bottom: 1px solid #000;
          margin: -4px -6px 4px;
          background: #f1f5f9; padding: 3px;
        }
        .approval-grid .sig-line {
          height: 26px; border-bottom: 1px solid #000;
          margin: 6px 0 1px;
        }
        .approval-grid .sig-fill {
          font-size: 11px; font-weight: 700;
          padding: 2px 0;
        }

        .remarks {
          margin-top: 6px; padding: 5px 8px;
          border: 1px solid #000; min-height: 30px;
        }
        .remarks .lbl { display: block; }

        .footer {
          margin-top: 6px; display: grid;
          grid-template-columns: 1.2fr 1fr 1fr 1fr;
          gap: 0; border: 1px solid #000;
        }
        .footer > div {
          padding: 4px 6px; border-right: 1px solid #000;
          font-size: 10px;
        }
        .footer > div:last-child { border-right: none; }
        .footer .opts { display: flex; gap: 10px; }

        .recipient-row {
          margin-top: 6px; padding: 5px 8px;
          border: 1px solid #000;
          font-size: 10.5px;
        }
        .recipient-row .row1 {
          display: flex; gap: 14px; padding-bottom: 2px;
        }

        @media print {
          body { padding: 0; }
          .no-print { display: none !important; }
          .status { position: static; display: inline-block; margin-bottom: 4px; }
        }
        .toolbar {
          position: fixed; top: 12px; right: 12px; display: flex; gap: 6px; z-index: 100;
        }
        .toolbar button {
          padding: 8px 14px; font-size: 12px; font-weight: 700;
          border: 1px solid #0f172a; background: #fff; color: #0f172a;
          border-radius: 4px; cursor: pointer;
        }
        .toolbar button.primary { background: #1565C0; color: #fff; border-color: #1565C0; }
      </style></head><body>
      <div class="toolbar no-print">
        <button onclick="window.print()" class="primary">Print / Save as PDF</button>
        <button onclick="window.close()">Close</button>
      </div>
      <div class="head">
        ${slogo ? `<img class="logo" src="${slogo}" alt="" />` : ''}
        <div class="name">
          <h1>${sname}</h1>
          <h2>Expenditure Approval Form</h2>
        </div>
        ${slogo ? `<div style="width:56px"></div>` : ''}
      </div>
      <div class="status ${isPosted ? 'posted' : isPending ? 'pending' : isRejected ? 'rejected' : ''}">
        ${esc(v.status || '')}
      </div>

      <!-- Top identification row -->
      <div class="id-grid">
        <div>
          <span class="lbl">Requisitioner's Name:</span>
          <div style="font-weight:700;min-height:14px">${esc(v.request_holder || items[0]?.request_holder || '')}</div>
        </div>
        <div>
          <span class="lbl">Suggested Supplier's Name:</span>
          <div style="min-height:14px"></div>
        </div>
        <div>
          <span class="lbl">Department:</span>
          <div style="min-height:14px"></div>
        </div>
        <div>
          <span class="lbl">Accts Dept Use</span>
          <div style="min-height:14px"></div>
        </div>
      </div>
      <div class="id-grid">
        <div>
          <span class="lbl">Date Form Submitted:</span>
          <div style="font-weight:700">${esc(dateSubmitted)}</div>
        </div>
        <div>
          <span class="lbl">Address / Tel #:</span>
          <div style="min-height:14px"></div>
        </div>
        <div>
          <span class="lbl">Date (Supplies / Services) Needed:</span>
          <div style="font-weight:700">${esc(dateNeeded)}</div>
        </div>
        <div>
          <span class="lbl">Code / Class:</span>
          <div style="min-height:14px"></div>
        </div>
      </div>

      <!-- Information regarding expenditure -->
      <div class="info-section">
        <div class="title">Information Regarding Expenditure</div>
        <div class="info-fields">
          <div class="row">
            <span class="lbl">Supplies for / Name of Event/ministry:</span>
            <div style="font-weight:700; min-height:14px">${esc(v.narration || items[0]?.narration || '')}</div>
          </div>
          <div class="row">
            <span class="lbl">Date:</span>
            <div style="font-weight:700">${esc(dateNeeded)}</div>
          </div>
          <div class="row">
            <span class="lbl">Time:</span>
            <div style="min-height:14px"></div>
          </div>
          <div class="row">
            <span class="lbl">Place:</span>
            <div style="min-height:14px"></div>
          </div>
          <div class="row" style="grid-column: 1 / -1">
            <span class="lbl">Reason(s) for expenditure:</span>
            <div style="font-weight:600; min-height:14px">
              ${items.map(it => esc(`${it.exp_ty} — ${it.narration || ''}`)).join('; ')}
            </div>
          </div>
        </div>

        <table class="items">
          <thead><tr>
            <th>ITEM No</th>
            <th>DESCRIPTION OF SUPPLIES / SERVICES NEEDED</th>
            <th>QUANTITY REQUIRED</th>
            <th>QUOTATIONS / ESTIMATES</th>
            <th class="right">UNIT PRICE</th>
            <th class="right">TOTAL COST<br/><span style="font-size:9px;font-weight:500">GHC</span></th>
          </tr></thead>
          <tbody>
            ${itemRows.join('')}
            <tr class="total">
              <td colspan="5">TOTAL</td>
              <td class="right">${fmtMoney(total)}</td>
            </tr>
          </tbody>
        </table>

        <!-- Classification check row -->
        <div class="check-row">
          <div>${checkbox(hasCat('supplies') || hasCat('food') || hasCat('stationary') || hasCat('book'))} <strong>This expenditure will be regarded as</strong></div>
          <div>${checkbox(hasCat('staff advance'))} Staff Advance (application attached)</div>
          <div>${checkbox(hasCat('reimbursement'))} Staff Reimbursement (invoice attached)</div>
          <div>${checkbox(hasCat('settlement') || hasCat('supplier'))} Settlement on Supplier's Invoice (proforma attached)</div>
        </div>

        <!-- Credit term -->
        <div class="credit-row">
          <strong style="margin-right:4px">Credit Term:</strong>
          <span class="opt">${checkbox(false)} 7 wk</span>
          <span class="opt">${checkbox(false)} 24 hr</span>
          <span class="opt">${checkbox(false)} 30 day</span>
          <span class="opt">${checkbox(false)} 60 day</span>
          <span class="opt">${checkbox(false)} 90 day</span>
        </div>

        <!-- Amount in words -->
        <div class="words">
          <span class="lbl">Amount in words:</span>
          <div class="v">${esc(numToWords(total))}</div>
        </div>
      </div>

      <!-- Payee -->
      <div class="payee">
        <span class="lbl">FULL NAME OF PAYEE TO APPEAR ON CHEQUE (please print):</span>
        <div style="font-weight:700;font-size:13px;letter-spacing:1px;min-height:18px">
          ${esc(items[0]?.request_holder || v.request_holder || '')}
        </div>
      </div>

      <!-- Three-tier approval.
           Each block is now a SEPARATE signature, taken from the
           expenditure request this voucher paid. Before this, one
           approval was printed into two of the three boxes, so a form
           that shows three stages was reporting one. A voucher raised
           without a request (approval switched off, or raised before
           requests existed) falls back to the single approver, and the
           unused blocks print blank rather than repeating a name. -->
      <div class="approval-grid">
        ${(() => {
          const req = v.request || null;
          const step = (id) => (req?.ladder || []).find(s => s.id === id) || null;
          // Fall back to the voucher's own single approval so old
          // vouchers still print something truthful.
          const legacy = { by: v.approved_by_name || '', at: v.approved_at };
          const block = (title, s, fallback) => {
            const by = s ? (s.done ? s.by : '') : (fallback ? fallback.by : '');
            const at = s ? (s.done ? s.at : null) : (fallback ? fallback.at : null);
            const waiting = s ? !s.done : (isPending && !!fallback);
            return `
        <div>
          <div class="col-title">${title}</div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px">
            <div>
              <span class="lbl">Name:</span>
              <div class="sig-fill">${waiting ? '— pending —' : esc(by || '')}</div>
            </div>
            <div>
              <span class="lbl">Signature:</span>
              <div class="sig-line"></div>
            </div>
          </div>
          <span class="lbl">Date:</span>
          <div class="sig-fill">${waiting ? '— pending —' : esc(at ? fmtDate(at) : '')}</div>
        </div>`;
          };
          return [
            block("Department Head's Endorsement", step('endorse'),
              req ? null : { by: v.created_by_name || '', at: dateSubmitted }),
            block('1st Level Approval', step('l1'), req ? null : legacy),
            block('Final Level Approval', step('final'), req ? null : legacy),
          ].join('');
        })()}
      </div>

      <!-- Remarks -->
      <div class="remarks">
        <span class="lbl">Remarks:</span>
        <div style="min-height:24px">${esc(v.reversal_reason || '')}</div>
      </div>

      <!-- Payment mode + recipient -->
      <div class="footer">
        <div>
          <strong>PAYMENT Mode:</strong>
          <div class="opts" style="margin-top:2px">
            <span>${checkbox(isCash)} Cash</span>
            <span>${checkbox(!isCash && (v.cheque_no || '').length > 0)} Cheque</span>
            <span>${checkbox(!isCash)} Bank</span>
          </div>
        </div>
        <div>
          <span class="lbl">PMT</span>
          <div style="font-weight:700">${esc(v.acc_name || '')}</div>
        </div>
        <div>
          <span class="lbl">Cheque No.</span>
          <div style="font-weight:700">${esc(v.cheque_no || '—')}</div>
        </div>
        <div>
          <span class="lbl">Date</span>
          <div style="font-weight:700">${esc(dateNeeded)}</div>
        </div>
      </div>

      <!-- Recipient sign-off -->
      <div class="recipient-row">
        <div class="row1" style="margin-bottom:6px">
          <strong>Received By (Name):</strong>
          <span style="flex:1;border-bottom:1px solid #000;font-weight:700;padding-left:6px">
            ${esc(items[0]?.request_holder || v.request_holder || '')}
          </span>
        </div>
        <div class="row1">
          <strong>Recipient's Signature:</strong>
          <span style="flex:1;border-bottom:1px solid #000">&nbsp;</span>
          <strong>Date:</strong>
          <span style="border-bottom:1px solid #000;display:inline-block;min-width:120px">&nbsp;</span>
        </div>
        <div style="font-size:9px;font-style:italic;margin-top:4px">
          This approval is valid until cancelled. Any evidence of part has been received by him/her.
        </div>
      </div>
      </body></html>`;
    w.document.write(html);
    w.document.close();
  };

  const printVoucher = (v) => {
    const w = window.open('', '_blank', 'width=920,height=1100');
    if (!w) return;
    const items = (v.items && v.items.length > 0)
      ? v.items
      : [{
          exp_cat: v.exp_cat, exp_ty: v.exp_ty,
          amount: Number(v.amount) || 0,
          narration: v.narration || '',
          request_holder: v.request_holder || '',
        }];
    const total = items.reduce((s, it) => s + Number(it.amount || 0), 0);
    const isCash = v.acc_name === CASH;

    const esc = (s) => String(s ?? '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

    const sname    = esc(school?.school_name || 'Your School');
    const smotto   = esc(school?.motto || '');
    const saddr    = esc(school?.address || '');
    const sphone   = esc(school?.phone || '');
    const semail   = esc(school?.email || '');
    const slogo    = school?.logo || '';

    const itemsRows = items.map((it, i) => `
      <tr>
        <td class="num">${i + 1}</td>
        <td><strong>${esc(it.exp_cat)}</strong><div class="sub">${esc(it.exp_ty)}</div></td>
        <td>${esc(it.narration || '—')}</td>
        <td>${esc(it.request_holder || '—')}</td>
        <td class="right">${fmtMoney(it.amount)}</td>
      </tr>`).join('');

    const html = `<!doctype html><html><head><title>${esc(v.voucher_no)}</title>
      <style>
        @page { size: A4; margin: 14mm; }
        * { box-sizing: border-box; }
        body {
          font-family: 'IBM Plex Sans', Inter, Arial, sans-serif;
          margin: 0; padding: 24px; color: #0f172a;
          position: relative; min-height: 100vh;
        }
        /* Logo watermark */
        .watermark {
          position: fixed; top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          opacity: 0.06; z-index: 0;
          width: 70%; pointer-events: none;
        }
        .watermark img { width: 100%; height: auto; }
        .content { position: relative; z-index: 1; }

        /* School header */
        .head {
          display: flex; align-items: center; gap: 16px;
          padding-bottom: 14px; border-bottom: 3px double #0f172a;
        }
        .head img.logo { width: 70px; height: 70px; object-fit: contain; }
        .head .name { flex: 1; }
        .head h1 {
          margin: 0; font-size: 22px; letter-spacing: 1px;
          text-transform: uppercase; font-weight: 800;
        }
        .head .motto { color: #475569; font-size: 12.5px; font-style: italic; margin-top: 2px; }
        .head .meta { color: #475569; font-size: 11px; margin-top: 4px; }
        .head .right { text-align: right; }
        .pv-no {
          font-family: 'IBM Plex Mono', ui-monospace, monospace;
          font-size: 13px; font-weight: 700; color: #1565C0;
          background: #E3F2FD; padding: 4px 10px; border-radius: 6px;
          display: inline-block;
        }
        .pv-status {
          display: inline-block; margin-top: 4px;
          font-size: 10px; font-weight: 800; letter-spacing: 1px;
          padding: 3px 8px; border-radius: 4px;
          background: ${v.status === 'POSTED' ? '#dcfce7' : v.status === 'PENDING' ? '#fef3c7' : v.status === 'REVERSED' ? '#fee2e2' : '#f1f5f9'};
          color: ${v.status === 'POSTED' ? '#166534' : v.status === 'PENDING' ? '#92400e' : v.status === 'REVERSED' ? '#991b1b' : '#334155'};
        }

        /* Title */
        h2.title {
          text-align: center; margin: 22px 0 4px;
          font-size: 18px; letter-spacing: 3px; text-transform: uppercase;
          font-weight: 800;
        }
        .title-rule {
          width: 60%; height: 2px; background: #0f172a;
          margin: 0 auto 16px;
        }

        /* Header info */
        .info {
          display: grid; grid-template-columns: 1fr 1fr 1fr 1fr;
          gap: 0; border: 1px solid #cbd5e1; margin-bottom: 16px;
        }
        .info > div {
          padding: 8px 12px; border-right: 1px solid #cbd5e1;
        }
        .info > div:last-child { border-right: none; }
        .info .lbl {
          font-size: 9.5px; font-weight: 800; text-transform: uppercase;
          letter-spacing: 0.6px; color: #64748b;
        }
        .info .val {
          font-size: 13px; font-weight: 700; color: #0f172a;
          margin-top: 2px;
        }

        /* T-account / items table */
        .t-wrap {
          display: grid; grid-template-columns: 1fr 1fr; gap: 0;
          border: 2px solid #0f172a; margin-bottom: 18px;
        }
        .t-side {
          padding: 0;
        }
        .t-side.dr { border-right: 2px solid #0f172a; background: #f8fafc; }
        .t-side.cr { background: #f0fdf4; }
        .t-head {
          padding: 8px 12px; text-align: center; font-weight: 800;
          font-size: 12px; letter-spacing: 2px;
          border-bottom: 2px solid #0f172a;
        }
        .t-head.dr { background: #1565C0; color: #fff; }
        .t-head.cr { background: #2e7d32; color: #fff; }

        table.items {
          width: 100%; border-collapse: collapse;
        }
        table.items th, table.items td {
          padding: 6px 10px; font-size: 11.5px; text-align: left;
          border-bottom: 1px solid #e2e8f0;
        }
        table.items th {
          background: #f1f5f9; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.5px; font-size: 9.5px;
          color: #475569;
        }
        table.items td.num { width: 24px; color: #94a3b8; font-weight: 800; text-align: center; }
        table.items td .sub { color: #64748b; font-size: 10px; margin-top: 1px; }
        table.items td.right {
          text-align: right; font-variant-numeric: tabular-nums;
          font-weight: 800;
        }

        .t-cr {
          padding: 14px 12px;
        }
        .t-cr .acct {
          font-size: 14px; font-weight: 800; color: #166534;
        }
        .t-cr .acct .sub { color: #64748b; font-size: 11px; font-weight: 500; margin-top: 2px; }
        .t-cr .amt {
          margin-top: 14px; font-size: 22px; font-weight: 800;
          font-variant-numeric: tabular-nums; color: #166534;
        }

        .total-row {
          background: #0f172a; color: #fff;
          padding: 10px 14px;
          display: flex; justify-content: space-between;
          font-weight: 800; letter-spacing: 1px; text-transform: uppercase;
          font-size: 13px;
        }
        .total-row .amt {
          font-size: 16px; font-variant-numeric: tabular-nums;
        }

        /* Cheque / narration footer */
        .footer-info {
          display: grid; grid-template-columns: 1fr 1fr;
          gap: 8px; margin: 14px 0;
        }
        .footer-info .box {
          border: 1px solid #cbd5e1; border-radius: 4px; padding: 8px 12px;
        }
        .footer-info .lbl {
          font-size: 9.5px; font-weight: 800; text-transform: uppercase;
          letter-spacing: 0.6px; color: #64748b;
        }
        .footer-info .val { font-size: 12.5px; margin-top: 2px; }

        /* Signatures */
        .sigs {
          display: grid; grid-template-columns: 1fr 1fr 1fr;
          gap: 28px; margin-top: 70px;
        }
        .sigs .col {
          text-align: center;
          border-top: 1.5px solid #0f172a;
          padding-top: 6px;
        }
        .sigs .name {
          font-size: 11px; font-weight: 800;
          text-transform: uppercase; letter-spacing: 1.2px;
        }
        .sigs .role {
          font-size: 9.5px; color: #64748b; margin-top: 2px;
        }

        /* Print mode */
        @media print {
          body { padding: 0; }
          .no-print { display: none !important; }
        }
        .toolbar {
          position: fixed; top: 12px; right: 12px; display: flex; gap: 6px;
          z-index: 100;
        }
        .toolbar button {
          padding: 8px 14px; font-size: 12px; font-weight: 700;
          border: 1px solid #0f172a; background: #fff; color: #0f172a;
          border-radius: 4px; cursor: pointer;
        }
        .toolbar button.primary { background: #1565C0; color: #fff; border-color: #1565C0; }
      </style></head><body>
      <div class="toolbar no-print">
        <button onclick="window.print()" class="primary">Print / Save as PDF</button>
        <button onclick="window.close()">Close</button>
      </div>
      ${slogo ? `<div class="watermark"><img src="${slogo}" alt="" /></div>` : ''}
      <div class="content">
        <div class="head">
          ${slogo ? `<img class="logo" src="${slogo}" alt="" />` : ''}
          <div class="name">
            <h1>${sname}</h1>
            ${smotto ? `<div class="motto">${smotto}</div>` : ''}
            <div class="meta">
              ${[saddr, sphone, semail].filter(Boolean).join(' · ')}
            </div>
          </div>
          <div class="right">
            <div class="pv-no">${esc(v.voucher_no)}</div><br/>
            <span class="pv-status">${esc(v.status || '')}</span>
          </div>
        </div>

        <h2 class="title">Payment Voucher</h2>
        <div class="title-rule"></div>

        <div class="info">
          <div>
            <div class="lbl">Date</div>
            <div class="val">${esc(fmtDate(v.pay_date))}</div>
          </div>
          <div>
            <div class="lbl">Period</div>
            <div class="val">${esc(v.mon_yer || '')}${v.term ? ' · ' + esc(v.term) : ''}${v.acc_yer ? ' · ' + esc(v.acc_yer) : ''}</div>
          </div>
          <div>
            <div class="lbl">Paid From</div>
            <div class="val">${esc(v.acc_name)}</div>
          </div>
          <div>
            <div class="lbl">${isCash ? 'Mode' : 'Cheque No.'}</div>
            <div class="val">${isCash ? 'Cash' : esc(v.cheque_no || '—')}</div>
          </div>
        </div>

        <div class="t-wrap">
          <div class="t-side dr">
            <div class="t-head dr">DEBIT (DR) — Items</div>
            <table class="items">
              <thead><tr>
                <th>#</th><th>Category / Type</th><th>Narration</th><th>Requester</th><th class="right">Amount (GHS)</th>
              </tr></thead>
              <tbody>${itemsRows}</tbody>
            </table>
          </div>
          <div class="t-side cr">
            <div class="t-head cr">CREDIT (CR) — Account</div>
            <div class="t-cr">
              <div class="acct">${esc(v.acc_name)}<div class="sub">${isCash ? 'Asset · Cash' : 'Asset · Bank'}</div></div>
              <div class="amt">GHS ${fmtMoney(total)}</div>
            </div>
          </div>
        </div>

        <div class="total-row">
          <span>Total · ${items.length} item${items.length === 1 ? '' : 's'}</span>
          <span class="amt">GHS ${fmtMoney(total)}</span>
        </div>

        ${v.narration ? `
          <div class="footer-info">
            <div class="box" style="grid-column: 1 / -1;">
              <div class="lbl">Cheque-level narration</div>
              <div class="val">${esc(v.narration)}</div>
            </div>
          </div>` : ''}

        <div class="sigs">
          <div class="col">
            <div class="name">${esc(v.created_by_name || '')}</div>
            <div class="role">Prepared by</div>
          </div>
          <div class="col">
            <div class="name">${esc(v.approved_by_name || (v.status === 'POSTED' && v.approved_at ? '' : ''))}</div>
            <div class="role">Approved by</div>
          </div>
          <div class="col">
            <div class="name">${esc(items[0]?.request_holder || v.request_holder || '')}</div>
            <div class="role">Received by</div>
          </div>
        </div>
      </div>
      </body></html>`;
    w.document.write(html);
    w.document.close();
  };

  // ── Derived ──
  const cih  = Number(snap?.cash_in_hand || 0);
  const bf   = snap?.brought_forward || { cash: { exists: false, amount: 0 }, banks: [] };
  const banks = snap?.banks || [];
  const byAcc = snap?.payments_by_account || [];
  const byCat = snap?.payments_by_category || [];
  const journal = snap?.journal || [];
  const period = snap?.period_payments || 0;

  const breakdown = useMemo(() => {
    const map = new Map();
    for (const r of byCat) {
      map.set(r.category || '—', (map.get(r.category || '—') || 0) + Number(r.total || 0));
    }
    return Array.from(map.entries()).map(([label, value]) => ({ label, value }));
  }, [byCat]);

  // Filtered/searched lists for the side-by-side tables.
  const inRange = (d, from, to) => {
    if (!from && !to) return true;
    const ds = String(d || '').slice(0, 10);
    if (!ds) return true;
    if (from && ds < from) return false;
    if (to   && ds > to)   return false;
    return true;
  };

  const filteredVouchers = useMemo(() => {
    const q = voucherQ.trim().toLowerCase();
    return vouchers.filter(v => {
      if (!inRange(v.pay_date, voucherFrom, voucherTo)) return false;
      if (!q) return true;
      const haystack = [
        v.voucher_no, v.acc_name, v.exp_cat, v.exp_ty,
        v.narration, v.cheque_no, v.request_holder, v.status,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [vouchers, voucherQ, voucherFrom, voucherTo]);

  const filteredJournal = useMemo(() => {
    const q = journalQ.trim().toLowerCase();
    return journal.filter(r => {
      if (!inRange(r.date, journalFrom, journalTo)) return false;
      if (!q) return true;
      const haystack = [
        r.account, r.voucher_no, r.receipt_no, r.type, r.category, r.narration, r.side,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [journal, journalQ, journalFrom, journalTo]);

  return (
    <Box sx={{ maxWidth: 1500, mx: 'auto', fontFamily: FONT_ACCT, pb: 4 }}>
      <ModuleHeader
        icon={<CashbookIcon />}
        title="Cashbook"
        subtitle="Opening balances, deposits, payments and the running journal — per month, per account"
      />

      {/* Subtle approval-queue link, visible only to users who can
          actually approve. The toggle itself lives on the Settings
          page (super_admin only) — regular cashier users don't see
          any approval-mode UI here. */}
      {approvalRequired && canApprove && (
        <Alert severity="warning" variant="outlined"
          sx={{ mb: 2, borderRadius: 2, alignItems: 'center' }}
          action={
            <Button size="small" variant="outlined" color="warning"
              href="/cashbook/approvals"
              sx={{ textTransform: 'none', fontWeight: 800 }}>
              Open queue
            </Button>
          }>
          Expenditure approval is ON — review pending Payment Vouchers in the approval queue.
        </Alert>
      )}

      {/* Month picker + filters */}
      <Card sx={{ mb: 2.5, borderRadius: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="center">
            <TextField select size="small" label="Working month"
              value={month} onChange={(e) => setMonth(e.target.value)}
              sx={{ minWidth: 240 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><MonthIcon fontSize="small" /></InputAdornment> }}>
              {(months.find(m => m.mon_yer === month) ? months : [...months, { mon_yer: month }])
                .map(m => (
                  <MenuItem key={m.mon_yer} value={m.mon_yer}>
                    {m.mon_yer}
                    {m.rows && <span style={{ color: '#888', fontSize: 11, marginLeft: 8 }}>· {m.rows} rows</span>}
                  </MenuItem>
                ))}
            </TextField>
            <TextField select size="small" label="Account filter (journal only)"
              value={accountFilter} onChange={(e) => setAccountFilter(e.target.value)}
              sx={{ minWidth: 220 }}>
              <MenuItem value="">All accounts</MenuItem>
              <MenuItem value={CASH}>Cash-In-Hand</MenuItem>
              {accounts.filter(a => a.name !== CASH).map(a => (
                <MenuItem key={a.name} value={a.name}>{a.name}</MenuItem>
              ))}
            </TextField>
            <Box sx={{ flex: 1 }} />
            {approvalRequired && (
              <Tooltip title="Every new Payment Voucher will be queued as PENDING and only posts to the cashbook after an approver acts on it.">
                <Chip size="small" color="warning" variant="filled"
                  label="APPROVAL MODE ON"
                  sx={{ fontWeight: 800, letterSpacing: 0.5,
                    '& .MuiChip-label': { px: 1.2 } }} />
              </Tooltip>
            )}
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" startIcon={<DepositIcon />}
                onClick={() => setDepOpen(true)}
                sx={{ fontWeight: 800, textTransform: 'none' }}>
                Cash → Bank
              </Button>
              <Button variant="outlined" startIcon={<TransferIcon />}
                onClick={() => setXferOpen(true)}
                sx={{ fontWeight: 800, textTransform: 'none' }}>
                Bank → Bank
              </Button>
              <Button variant="contained"
                color={approvalRequired ? 'warning' : 'error'}
                startIcon={<PaymentIcon />}
                onClick={() => { setPvEditing(null); setPvOpen(true); }}
                sx={{ fontWeight: 800, textTransform: 'none' }}>
                {approvalRequired ? 'Queue Payment for Approval' : 'New Payment'}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* KPI strip */}
      <Box sx={{ display: 'grid', gap: 1.5, mb: 2.5,
        gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' } }}>
        <KpiTile icon={<CashIcon />} label="Cash in Hand"
          value={`GHS ${fmtMoney(cih)}`}
          tone={cih > 0 ? 'success' : 'warning'}
          sub={bf.cash?.exists ? `Cash B/F: GHS ${fmtMoney(bf.cash.amount)}` : 'No cash B/F set'} />
        <KpiTile icon={<BankIcon />} label="Bank Balance"
          value={`GHS ${fmtMoney(snap?.bank_total || 0)}`} tone="primary"
          sub={`${banks.length} account${banks.length === 1 ? '' : 's'}`} />
        <KpiTile icon={<InflowIcon />} label="Deposits This Month"
          value={`GHS ${fmtMoney(snap?.period_deposits || 0)}`} tone="secondary" />
        <KpiTile icon={<OutflowIcon />} label="Payments This Month"
          value={`GHS ${fmtMoney(snap?.period_payments || 0)}`} tone="error" />
      </Box>

      {loading && (
        <Card sx={{ p: 6, textAlign: 'center', mb: 2 }}><CircularProgress /></Card>
      )}

      {!loading && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2.5 }}>

          {/* Opening balances */}
          <Card sx={{ borderRadius: 2 }}>
            <Box sx={{ px: 2.5, py: 1.4, borderBottom: 1, borderColor: 'divider',
              display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper' }}>
              <BFIcon fontSize="small" color="primary" />
              <Typography variant="subtitle2" fontWeight={800}>Opening Balances · {month}</Typography>
            </Box>
            <CardContent>
              {/* Cash B/F */}
              <SourceRow
                title="Cash-In-Hand B/F"
                avatar={<CashIcon fontSize="small" />}
                amount={bf.cash?.amount || 0}
                source={bf.cash?.source}
                bf_from={bf.cash?.bf_from}
                tone="success"
                onConfirm={bf.cash?.source === 'confirmed' ? null : bringForwardCash}
              />

              {/* Per-bank B/F */}
              <Typography variant="caption" color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 10, display: 'block', mb: 0.6, mt: 1 }}>
                Bank Accounts
              </Typography>
              {(bf.banks || []).length === 0 ? (
                <Alert severity="warning" variant="outlined" sx={{ borderRadius: 1.5 }}>
                  No bank accounts configured. Add one in Settings → Finance → Bank/Cash.
                </Alert>
              ) : (
                (bf.banks || []).map(b => (
                  <SourceRow
                    key={b.account}
                    title={b.account}
                    avatar={<BankIcon fontSize="small" />}
                    amount={b.amount || 0}
                    source={b.source}
                    bf_from={b.bf_from}
                    tone="primary"
                    dense
                    onConfirm={b.source === 'confirmed' ? null : () => bringForwardBank(b.account)}
                  />
                ))
              )}
            </CardContent>
          </Card>

          {/* Bank position */}
          <Card sx={{ borderRadius: 2 }}>
            <Box sx={{ px: 2.5, py: 1.4, borderBottom: 1, borderColor: 'divider',
              display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper' }}>
              <BankIcon fontSize="small" color="primary" />
              <Typography variant="subtitle2" fontWeight={800}>Bank Position · {month}</Typography>
              <Box sx={{ flex: 1 }} />
              <Chip size="small" variant="outlined"
                label={`${banks.length} account${banks.length === 1 ? '' : 's'}`} sx={{ fontWeight: 700 }} />
            </Box>
            <TableContainer>
              <Table size="small" sx={{
                '& .MuiTableCell-head': { bgcolor: 'grey.50', fontWeight: 800, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 },
              }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Account</TableCell>
                    <TableCell align="right">Opening</TableCell>
                    <TableCell align="right">Deposits</TableCell>
                    <TableCell align="right">Payments</TableCell>
                    <TableCell align="right">Closing</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {banks.length === 0 && (
                    <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No bank movement this month yet.
                    </TableCell></TableRow>
                  )}
                  {banks.map(b => {
                    const meta = sourceMeta(b.opening_source);
                    return (
                      <TableRow key={b.account} hover sx={{ '& td': { fontSize: 12, py: 0.85, ...TNUM } }}>
                        <TableCell sx={{ fontWeight: 700, fontFamily: FONT_ACCT }}>{b.account}</TableCell>
                        <TableCell align="right">
                          <Tooltip title={`${meta.label}${b.opening_from ? ` · from ${b.opening_from}` : ''}`}>
                            <span>GHS {fmtMoney(b.opening)}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'success.dark' }}>GHS {fmtMoney(b.deposits)}</TableCell>
                        <TableCell align="right" sx={{ color: 'error.main' }}>GHS {fmtMoney(b.payments)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, color: 'primary.main' }}>
                          GHS {fmtMoney(b.balance)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Box>
      )}

      {/* Where the money went */}
      {!loading && (period > 0 || byCat.length > 0) && (
        <Card sx={{ mt: 2.5, borderRadius: 2 }}>
          <Box sx={{ px: 2.5, py: 1.4, borderBottom: 1, borderColor: 'divider',
            display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper' }}>
            <OutflowIcon fontSize="small" color="error" />
            <Typography variant="subtitle2" fontWeight={800}>Where the money went · {month}</Typography>
            <Box sx={{ flex: 1 }} />
            <Typography fontWeight={800} sx={{ ...TNUM, color: 'error.main' }}>GHS {fmtMoney(period)}</Typography>
          </Box>
          <CardContent>
            <BreakdownBar data={breakdown} total={Math.max(period, 1)} />
          </CardContent>
        </Card>
      )}

      {/* Expenditure Requests — ask before you spend.
          Placed ABOVE the journal because when approval is switched on
          this is where the work starts: nothing reaches the journal
          below until a request here has been signed off. Shown even
          when the switch is off, so a school can raise requests
          voluntarily and see how it works before enforcing it. */}
      {!loading && (
        <Box sx={{ mt: 2.5 }}>
          <ExpenditureRequestsPanel
            categories={categories}
            expTypes={expTypes}
            refreshKey={reqRefresh}
            onPay={(r) => { setPvEditing(null); setPvRequest(r); setPvOpen(true); }}
          />
        </Box>
      )}

      {/* Running Journal + Payment Vouchers — side by side */}
      {!loading && (
        <Box sx={{ mt: 2.5, display: 'grid',
          gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 2.5,
          alignItems: 'start' }}>

          {/* Running Journal — LEFT */}
          <LedgerCard
            title={`Running Journal · ${month}`}
            subtitle={accountFilter ? `Filtered to ${accountFilter}` : 'Every line in date order'}
            icon={<CashbookIcon fontSize="small" />}
            tone="primary"
            count={filteredJournal.length}
            search={journalQ}
            onSearch={(v) => { setJournalQ(v); setJournalPage(0); }}
            placeholder="Search account, description…"
            dateFrom={journalFrom}
            dateTo={journalTo}
            onDateFrom={(v) => { setJournalFrom(v); setJournalPage(0); }}
            onDateTo={(v) => { setJournalTo(v); setJournalPage(0); }}
          >
            {isMobile ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1 }}>
                {filteredJournal.length === 0 && (
                  <Typography sx={{ p: 4, textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }}>
                    {journalQ ? `No rows match "${journalQ}"` : 'No movements yet for this filter.'}
                  </Typography>
                )}
                {filteredJournal
                  .slice(journalPage * journalRpp, (journalPage + 1) * journalRpp)
                  .map(r => {
                    const isBF = r.side === 'cbf' || r.side === 'bbf';
                    const inAmt  = Math.max(Number(r.receipt || 0), Number(r.deposit || 0));
                    const outAmt = Number(r.payment || 0);
                    const isIn = inAmt > 0;
                    const desc = r.voucher_no
                      ? `${r.voucher_no} · ${r.type || r.category || ''} · ${r.narration || ''}`
                      : r.petty_reimbursement_no
                        ? `Petty Cash Reimbursement · ${r.petty_mon_yer || ''} · ${r.petty_reason || ''}`
                      : r.side === 'cbf' ? 'Cash B/F'
                      : r.side === 'bbf' ? `Bank B/F (${r.account})`
                      : (r.receipt > 0 ? `Receipt · ${r.receipt_no || ''}`
                         : r.deposit > 0 ? `Cash → ${r.account}`
                         : '—');
                    // A transfer's incoming leg also has deposit > 0 on a
                    // bank account, so it must be claimed FIRST or the
                    // deposit-reverse button would fire at it and be
                    // rejected by the server.
                    const isXfer = r.type === 'Transfer' && !isBF;
                    const canReverseDeposit = !isXfer
                      && r.deposit > 0 && r.account !== CASH && !isBF;
                    return (
                      <MobileDataCard key={r.id}
                        accent={isBF ? '#1565C0' : (isIn ? '#2e7d32' : '#d32f2f')}
                        title={
                          <Stack direction="row" alignItems="center" spacing={0.7}>
                            {isBF && (
                              <Chip size="small" color="primary" label="B/F"
                                sx={{ height: 16, fontSize: 9, fontWeight: 800,
                                  '& .MuiChip-label': { px: 0.6 } }} />
                            )}
                            <span>{r.account}</span>
                          </Stack>
                        }
                        subtitle={`${r.date} · ${desc}`}
                        amount={isIn ? `+ ${fmtMoney(inAmt)}` : `- ${fmtMoney(outAmt)}`}
                        amountColor={isIn ? 'success.dark' : 'error.main'}
                        amountLabel={isIn ? 'In' : 'Out'}
                        actions={(r.transaction_id || canReverseDeposit || isXfer) ? (
                          <>
                            {r.transaction_id && (
                              <Button size="small" variant="text" color="info"
                                startIcon={<DoubleEntryIcon fontSize="small" />}
                                onClick={() => openDoubleEntry(
                                  r.transaction_id,
                                  `${r.account} · ${r.date} · ${desc}`
                                )}
                                sx={{ textTransform: 'none', fontWeight: 700 }}>
                                DR/CR
                              </Button>
                            )}
                            {canReverseDeposit && (
                              <Button size="small" variant="outlined" color="error"
                                startIcon={<DeleteIcon fontSize="small" />}
                                onClick={() => reverseDeposit(r)}
                                sx={{ textTransform: 'none', fontWeight: 700 }}>
                                Reverse
                              </Button>
                            )}
                            {isXfer && (
                              <Button size="small" variant="outlined" color="error"
                                startIcon={<DeleteIcon fontSize="small" />}
                                onClick={() => reverseTransfer(r)}
                                sx={{ textTransform: 'none', fontWeight: 700 }}>
                                Reverse
                              </Button>
                            )}
                          </>
                        ) : null}
                      />
                    );
                  })}
              </Box>
            ) : (
            <TableContainer sx={{ maxHeight: 520, bgcolor: 'background.paper' }}>
              <Table stickyHeader size="small" sx={{
                '& .MuiTableCell-head': {
                  bgcolor: '#fafbfc', fontWeight: 800, fontSize: 10.5,
                  textTransform: 'uppercase', letterSpacing: 0.6,
                  color: 'text.secondary',
                  borderBottom: '2px solid', borderColor: 'primary.light',
                  py: 1.1,
                },
              }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Account</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">In</TableCell>
                    <TableCell align="right">Out</TableCell>
                    <TableCell align="center" sx={{ width: 50 }}>DR/CR</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredJournal.length === 0 && (
                    <TableRow><TableCell colSpan={6} align="center"
                      sx={{ py: 5, color: 'text.secondary', fontStyle: 'italic' }}>
                      {journalQ ? `No rows match "${journalQ}"` : 'No movements yet for this filter.'}
                    </TableCell></TableRow>
                  )}
                  {filteredJournal
                    .slice(journalPage * journalRpp, (journalPage + 1) * journalRpp)
                    .map(r => {
                      const isBF = r.side === 'cbf' || r.side === 'bbf';
                      // Legacy LedgerService writes the same value into
                      // both `receipt` and `depo_amount` for every fee
                      // receipt — adding them double-counts. Use the
                      // larger of the two so a single 250 GHS receipt
                      // shows as 250, not 500.
                      const inAmt  = Math.max(Number(r.receipt || 0), Number(r.deposit || 0));
                      const outAmt = Number(r.payment || 0);
                      const desc = r.voucher_no
                        ? `${r.voucher_no} · ${r.type || r.category || ''} · ${r.narration || ''}`
                        : r.petty_reimbursement_no
                          ? `Petty Cash Reimbursement · ${r.petty_mon_yer || ''} · ${r.petty_reason || ''}`
                        : r.side === 'cbf' ? 'Cash B/F'
                        : r.side === 'bbf' ? `Bank B/F (${r.account})`
                        : (r.receipt > 0 ? `Receipt · ${r.receipt_no || ''}`
                           : r.deposit > 0 ? `Cash → ${r.account}`
                           : '—');
                      return (
                        <TableRow key={r.id} hover sx={{
                          '& td': { fontSize: 11.5, py: 0.85, ...TNUM,
                            borderBottom: '1px solid', borderColor: 'rgba(0,0,0,0.04)' },
                          bgcolor: isBF
                            ? (t) => alpha(t.palette.primary.main, 0.06)
                            : '#ffffff',
                          '&:nth-of-type(even)': isBF ? {} : { bgcolor: '#fcfdfe' },
                          '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.04) + ' !important' },
                        }}>
                          <TableCell sx={{ color: 'text.secondary' }}>{r.date}</TableCell>
                          <TableCell sx={{ fontWeight: 700, maxWidth: 140,
                            overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            <Tooltip title={r.account}><span>{r.account}</span></Tooltip>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            <Tooltip title={desc}><span>{desc}</span></Tooltip>
                          </TableCell>
                          <TableCell align="right"
                            sx={{ color: inAmt > 0 ? 'success.dark' : 'text.disabled',
                                  fontWeight: inAmt > 0 ? 700 : 400 }}>
                            {inAmt > 0 ? fmtMoney(inAmt) : '—'}
                          </TableCell>
                          <TableCell align="right"
                            sx={{ color: outAmt > 0 ? 'error.main' : 'text.disabled',
                                  fontWeight: outAmt > 0 ? 700 : 400 }}>
                            {outAmt > 0 ? fmtMoney(outAmt) : '—'}
                          </TableCell>
                          <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                            {r.transaction_id && (
                              <Tooltip title="View double-entry (DR/CR)">
                                <IconButton size="small" color="info"
                                  onClick={() => openDoubleEntry(
                                    r.transaction_id,
                                    `${r.account} · ${r.date} · ${desc}`
                                  )}>
                                  <DoubleEntryIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {/* Reverse a Cash→Bank deposit row directly
                                from the journal. Identified by deposit>0
                                and a non-cash account (which excludes
                                bbf/cbf opening rows and cash receipts).
                                Transfers are checked first: their
                                incoming leg also looks like a deposit,
                                but it has to go through the transfer
                                reversal so BOTH legs are undone. */}
                            {r.type === 'Transfer' && !isBF ? (
                              <Tooltip title="Reverse this transfer (undoes both accounts)">
                                <IconButton size="small" color="error"
                                  onClick={() => reverseTransfer(r)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : r.deposit > 0 && r.account !== CASH && !isBF ? (
                              <Tooltip title="Reverse this deposit">
                                <IconButton size="small" color="error"
                                  onClick={() => reverseDeposit(r)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            )}
            <TablePagination
              component="div"
              count={filteredJournal.length}
              page={journalPage}
              onPageChange={(_, p) => setJournalPage(p)}
              rowsPerPage={journalRpp}
              onRowsPerPageChange={(e) => { setJournalRpp(Number(e.target.value)); setJournalPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{ borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
            />
          </LedgerCard>

          {/* Payment Vouchers — RIGHT */}
          <LedgerCard
            title={`Payment Vouchers · ${month}`}
            subtitle="Disbursements logged this month"
            icon={<VoucherIcon fontSize="small" />}
            tone="error"
            count={filteredVouchers.length}
            search={voucherQ}
            onSearch={(v) => { setVoucherQ(v); setVoucherPage(0); }}
            placeholder="Search PV no, account, narration, requester…"
            dateFrom={voucherFrom}
            dateTo={voucherTo}
            onDateFrom={(v) => { setVoucherFrom(v); setVoucherPage(0); }}
            onDateTo={(v) => { setVoucherTo(v); setVoucherPage(0); }}
          >
            {isMobile ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1 }}>
                {filteredVouchers.length === 0 && (
                  <Typography sx={{ p: 4, textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }}>
                    {voucherQ ? `No vouchers match "${voucherQ}"` : 'No payment vouchers this month.'}
                  </Typography>
                )}
                {filteredVouchers
                  .slice(voucherPage * voucherRpp, (voucherPage + 1) * voucherRpp)
                  .map(v => {
                    const isReversed = v.status === 'REVERSED';
                    const isEdited   = v.status === 'EDITED';
                    const itemsCount = (v.items || []).length;
                    return (
                      <MobileDataCard key={v.id}
                        accent={isReversed ? '#d32f2f' : isEdited ? '#9e9e9e' : '#2e7d32'}
                        title={v.voucher_no}
                        subtitle={`${fmtDate(v.pay_date)} · ${v.acc_name}`}
                        status={v.status}
                        statusColor={isReversed ? 'error' : isEdited ? 'default' : 'success'}
                        fields={[
                          { label: 'Type',
                            value: itemsCount > 1
                              ? `${v.exp_ty} (+${itemsCount - 1} more)`
                              : v.exp_ty,
                            bold: true },
                          { label: 'Category', value: v.exp_cat },
                        ]}
                        amount={`GHS ${fmtMoney(v.amount)}`}
                        amountColor="error.main"
                        actions={(
                          <>
                            <Button size="small" variant="text" color="info"
                              startIcon={<DoubleEntryIcon fontSize="small" />}
                              onClick={() => openDoubleEntry(
                                v.transaction_id,
                                `${v.voucher_no} · ${v.acc_name} · GHS ${fmtMoney(v.amount)}`
                              )}
                              sx={{ textTransform: 'none', fontWeight: 700, minWidth: 0 }}>
                              DR/CR
                            </Button>
                            <Button size="small" variant="text"
                              startIcon={<PrintIcon fontSize="small" />}
                              onClick={() => printVoucher(v)}
                              sx={{ textTransform: 'none', fontWeight: 700, minWidth: 0 }}>
                              PV
                            </Button>
                            <Button size="small" variant="text" color="warning"
                              startIcon={<DescriptionIcon fontSize="small" />}
                              onClick={() => printApprovalForm(v)}
                              sx={{ textTransform: 'none', fontWeight: 700, minWidth: 0 }}>
                              Form
                            </Button>
                            {!isReversed && !isEdited && (
                              <>
                                <Button size="small" variant="outlined" color="primary"
                                  startIcon={<EditIcon fontSize="small" />}
                                  onClick={() => { setPvEditing(v); setPvOpen(true); }}
                                  sx={{ textTransform: 'none', fontWeight: 700 }}>
                                  Edit
                                </Button>
                                <Button size="small" variant="outlined" color="error"
                                  startIcon={<DeleteIcon fontSize="small" />}
                                  onClick={() => removeVoucher(v)}
                                  sx={{ textTransform: 'none', fontWeight: 700 }}>
                                  Reverse
                                </Button>
                              </>
                            )}
                          </>
                        )}
                      />
                    );
                  })}
              </Box>
            ) : (
            <TableContainer sx={{ maxHeight: 520, bgcolor: 'background.paper' }}>
              <Table stickyHeader size="small" sx={{
                '& .MuiTableCell-head': {
                  bgcolor: '#fafbfc', fontWeight: 800, fontSize: 10.5,
                  textTransform: 'uppercase', letterSpacing: 0.6,
                  color: 'text.secondary',
                  borderBottom: '2px solid', borderColor: 'error.light',
                  py: 1.1,
                },
              }}>
                <TableHead>
                  <TableRow>
                    <TableCell>PV No.</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Account</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right" sx={{ width: 130 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredVouchers.length === 0 && (
                    <TableRow><TableCell colSpan={7} align="center"
                      sx={{ py: 5, color: 'text.secondary', fontStyle: 'italic' }}>
                      {voucherQ ? `No vouchers match "${voucherQ}"` : 'No payment vouchers this month.'}
                    </TableCell></TableRow>
                  )}
                  {filteredVouchers
                    .slice(voucherPage * voucherRpp, (voucherPage + 1) * voucherRpp)
                    .map(v => {
                      const isReversed = v.status === 'REVERSED';
                      const isEdited   = v.status === 'EDITED';
                      return (
                        <TableRow key={v.id} hover sx={{
                          '& td': { fontSize: 12, py: 0.85, ...TNUM,
                            borderBottom: '1px solid', borderColor: 'rgba(0,0,0,0.04)' },
                          opacity: isReversed || isEdited ? 0.55 : 1,
                          bgcolor: '#ffffff',
                          '&:nth-of-type(even)': { bgcolor: '#fcfdfe' },
                          '&:hover': { bgcolor: (t) => alpha(t.palette.error.main, 0.04) + ' !important' },
                        }}>
                          <TableCell sx={{ fontWeight: 700, fontFamily: FONT_MONO, fontSize: 11.5 }}>
                            {v.voucher_no}
                          </TableCell>
                          <TableCell>{fmtDate(v.pay_date)}</TableCell>
                          <TableCell sx={{ fontWeight: 700, maxWidth: 180,
                            overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            <Tooltip title={v.acc_name}><span>{v.acc_name}</span></Tooltip>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 180,
                            overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            <Tooltip title={
                              (v.items && v.items.length > 1)
                                ? v.items.map(it =>
                                    `${it.exp_cat} · ${it.exp_ty} · GHS ${fmtMoney(it.amount)}${it.narration ? ` · ${it.narration}` : ''}`
                                  ).join('\n')
                                : `${v.exp_cat} · ${v.exp_ty} · ${v.narration || ''}`
                            }>
                              <Box>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                  <Typography variant="body2" fontWeight={700} fontSize={12} noWrap>
                                    {v.exp_ty}
                                  </Typography>
                                  {v.items && v.items.length > 1 && (
                                    <Chip size="small" color="warning" variant="outlined"
                                      label={`+${v.items.length - 1}`}
                                      sx={{ height: 16, fontSize: 9.5, fontWeight: 800,
                                        '& .MuiChip-label': { px: 0.5 } }} />
                                  )}
                                </Stack>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {v.exp_cat}
                                </Typography>
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 800, color: 'error.main' }}>
                            GHS {fmtMoney(v.amount)}
                          </TableCell>
                          <TableCell>
                            <Chip size="small"
                              color={isReversed ? 'error' : isEdited ? 'default' : 'success'}
                              label={v.status} sx={{ fontWeight: 800, height: 19, fontSize: 9.5 }} />
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            <Tooltip title="View double-entry (DR/CR)">
                              <IconButton size="small" color="info"
                                onClick={() => openDoubleEntry(
                                  v.transaction_id,
                                  `${v.voucher_no} · ${v.acc_name} · GHS ${fmtMoney(v.amount)}`
                                )}>
                                <DoubleEntryIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Print Payment Voucher">
                              <IconButton size="small" onClick={() => printVoucher(v)}>
                                <PrintIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Print Expenditure Approval Form">
                              <IconButton size="small" color="warning"
                                onClick={() => printApprovalForm(v)}>
                                <DescriptionIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {!isReversed && !isEdited && (
                              <>
                                <Tooltip title="Edit">
                                  <IconButton size="small" color="primary"
                                    onClick={() => { setPvEditing(v); setPvOpen(true); }}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Reverse">
                                  <IconButton size="small" color="error" onClick={() => removeVoucher(v)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            )}
            <TablePagination
              component="div"
              count={filteredVouchers.length}
              page={voucherPage}
              onPageChange={(_, p) => setVoucherPage(p)}
              rowsPerPage={voucherRpp}
              onRowsPerPageChange={(e) => { setVoucherRpp(Number(e.target.value)); setVoucherPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{ borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
            />
          </LedgerCard>
        </Box>
      )}

      {/* ── Bank Transfers ─────────────────────────────────────────
          Full width, below the journal. One row per transfer rather
          than the journal's two legs, because this is the screen you
          come to when you want to UNDO one — and hunting for both
          halves of a transfer is how the wrong thing gets reversed. */}
      {!loading && (
        <Box sx={{ mt: 2 }}>
          <LedgerCard
            title={`Bank Transfers · ${month}`}
            subtitle="Money moved between the school's own bank accounts"
            icon={<TransferIcon fontSize="small" />}
            tone="info"
            count={transfers.length}
          >
            {transfers.length === 0 ? (
              <Typography sx={{ p: 4, textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }}>
                No bank-to-bank transfers in {month}.
              </Typography>
            ) : (
              <TableContainer sx={{ bgcolor: 'background.paper' }}>
                <Table size="small" sx={{
                  '& .MuiTableCell-head': {
                    bgcolor: '#fafbfc', fontWeight: 800, fontSize: 10.5,
                    textTransform: 'uppercase', letterSpacing: 0.6,
                    color: 'text.secondary',
                    borderBottom: '2px solid', borderColor: 'info.light',
                  },
                }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>From</TableCell>
                      <TableCell>To</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Reference</TableCell>
                      <TableCell>Narration</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transfers.map(x => {
                      const reversed = x.status === 'REVERSED';
                      return (
                        <TableRow key={x.transaction_id} hover
                          sx={reversed ? { opacity: 0.55 } : undefined}>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{x.date}</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'error.main', fontSize: 12.5 }}>
                            {x.from_account || '—'}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'success.dark', fontSize: 12.5 }}>
                            {x.to_account || '—'}
                          </TableCell>
                          <TableCell align="right" sx={{
                            ...TNUM, fontWeight: 800,
                            textDecoration: reversed ? 'line-through' : 'none',
                          }}>
                            {fmtMoney(x.amount)}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{x.reference || '—'}</TableCell>
                          <TableCell sx={{ fontSize: 12, maxWidth: 240 }}>
                            {x.narration || '—'}
                          </TableCell>
                          <TableCell>
                            <Chip size="small"
                              label={reversed ? 'Reversed' : (x.complete ? 'Posted' : 'Incomplete')}
                              color={reversed ? 'default' : (x.complete ? 'success' : 'warning')}
                              variant={reversed ? 'outlined' : 'filled'}
                              sx={{ height: 20, fontSize: 10.5, fontWeight: 800 }} />
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              {x.transaction_id && (
                                <Tooltip title="View double-entry (DR/CR)">
                                  <IconButton size="small" color="info"
                                    onClick={() => openDoubleEntry(
                                      x.transaction_id,
                                      `${x.from_account} → ${x.to_account} · ${x.date}`
                                    )}>
                                    <DoubleEntryIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {x.can_reverse ? (
                                <Tooltip title="Reverse this transfer — puts the money back">
                                  <IconButton size="small" color="error"
                                    onClick={() => reverseTransferRow(x)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <Tooltip title={reversed
                                  ? 'Already reversed'
                                  : 'One leg of this transfer is missing — fix it by hand'}>
                                  <span>
                                    <IconButton size="small" disabled>
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </LedgerCard>
        </Box>
      )}

      <DepositDialog open={depOpen} onClose={() => setDepOpen(false)}
        accounts={accounts} defaultMonth={month}
        cashInHand={cih}
        onSaved={() => { loadSnapshot(); loadVouchers(); }} />
      <TransferDialog open={xferOpen} onClose={() => setXferOpen(false)}
        accounts={accounts} banks={banks}
        onSaved={() => { loadSnapshot(); loadVouchers(); loadTransfers(); }} />
      <PaymentDialog open={pvOpen}
        onClose={() => { setPvOpen(false); setPvEditing(null); setPvRequest(null); }}
        accounts={accounts} categories={categories} expTypes={expTypes}
        editing={pvEditing} request={pvRequest}
        onSaved={async (res) => {
          // Surface clear feedback to the cashier so they know whether
          // the voucher hit the ledger or is sitting in the approval
          // queue. The backend always returns `status` on response.
          if (res?.status === 'PENDING') {
            await swal.success(
              `${res.voucher_no || 'Voucher'} queued for approval`,
              'Expenditure approval is ON — the cashbook + journal will only update after an approver acts on it. Track it under "Approve Cashbook Payments".'
            );
            // New pending item — bump the sidebar badge immediately.
            window.dispatchEvent(new Event('pendingCountsChanged'));
          } else if (res?.voucher_no) {
            await swal.success(`${res.voucher_no} posted`);
          }
          loadSnapshot();
          loadVouchers();
          // A paid request leaves "Ready to pay" — refresh the panel so
          // nobody is tempted to pay it a second time.
          if (pvRequest) setReqRefresh(k => k + 1);
        }} />
      <DoubleEntryDialog open={deOpen} onClose={() => setDeOpen(false)}
        uuid={deUuid} label={deLabel} />
    </Box>
  );
}
